import SearchableSelect from '../../../shared/components/SearchableSelect'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
  import { KTSVG } from '../../../../_metronic/helpers'
  import { toast, ToastContainer } from 'react-toastify'
  import { useAuth } from '../../auth'
  import {
    DailyPositionReportRecord,
    DprActivity,
    DprRobRow,
    DprLubeOilStatusRow,
    DprWeatherRow,
    DprTides,
    DprDowntimeRow,
    DprPobRow,
    DprSafetyRow,
    DprEquipmentRow,
    DprPassengerRow,
    DprFoConsumptionRow,
    DprMachineryFoConsumptionRow,
    DprEngineParamRow,
    DprExhTempRow,
    DprEngineRunningHoursRow,
    blankActivity,
    blankSummary,
    blankTides,
    blankDowntimeRow,
    blankLubeOilStatusRow,
    blankPassengerRow,
    blankFoConsumptionRow,
    blankMachineryFoConsumptionRow,
    DEFAULT_ROB_ROWS,
    DEFAULT_WEATHER_ROWS,
    DEFAULT_POB_ROWS,
    DEFAULT_SAFETY_LEFT,
    DEFAULT_SAFETY_RIGHT,
    normalizeEquipmentRows,
    DEFAULT_ENGINE_PARAMS,
    DEFAULT_EXH_TEMPS,
    DEFAULT_ENGINE_RUNNING_HOURS,
  } from '../core/_dprModels'
  import { getVoyageList, getVoyagesByVessel } from '../core/_requests'
  import {
    sanitizeCoordinatePart,
    validateDprShipPositionFinal,
    parseFleetDprDlPositionToDecimals,
  } from '../../../shared/utils/coordinateParsing'
  import DprMiniMap from './DprMiniMap'
  import { useDprRealtime } from '../core/useDprRealtime'
  import { useDprAutoSave } from '../core/useDprAutoSave'
  import { DprRealtimeStatusBar } from './DprRealtimeStatusBar'
  import { DprAuditHistoryDrawer } from './DprAuditHistoryDrawer'

  /* ────────── Date Formatter ────────── */
  const formatDateValues = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const FormattedDateInput = ({ value, onChange, className }: { value: string, onChange: (val: string) => void, className: string }) => {
    const [isEditing, setIsEditing] = useState(false);

    if (isEditing) {
      return (
        <input
          type='date'
          className={className}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setIsEditing(false)}
          autoFocus
        />
      )
    }

    return (
      <input
        type='text'
        className={className}
        value={formatDateValues(value)}
        onFocus={() => setIsEditing(true)}
        readOnly
      />
    )
  }

  const normalizePassengerRows = (rows: DprPassengerRow[]) =>
    rows.map((row, index) => ({
      ...row,
      srNo: index + 1,
    }))

  interface DprPositionFields {
    startLatDeg: string
    startLatMin: string
    startLatSec: string
    startLatDir: 'N' | 'S'
    startLonDeg: string
    startLonMin: string
    startLonSec: string
    startLonDir: 'E' | 'W'
    stopLatDeg: string
    stopLatMin: string
    stopLatSec: string
    stopLatDir: 'N' | 'S'
    stopLonDeg: string
    stopLonMin: string
    stopLonSec: string
    stopLonDir: 'E' | 'W'
  }

  type DprPositionErrors = Partial<Record<keyof DprPositionFields, string>>





  const blankPositionFields = (): DprPositionFields => ({
    startLatDeg: '',
    startLatMin: '',
    startLatSec: '',
    startLatDir: 'N',
    startLonDeg: '',
    startLonMin: '',
    startLonSec: '',
    startLonDir: 'E',
    stopLatDeg: '',
    stopLatMin: '',
    stopLatSec: '',
    stopLatDir: 'N',
    stopLonDeg: '',
    stopLonMin: '',
    stopLonSec: '',
    stopLonDir: 'E',
  })

  const parsePositionValue = (value?: string | null): DprPositionFields => {
    const defaults = blankPositionFields()
    const text = String(value ?? '').trim()
    if (!text) return defaults

    const extract = (label: string) => {
      const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const pattern = new RegExp(
        `${escapedLabel}:\\s*([^|]+?)\\s+Deg\\s+([^|]+?)\\s+min\\s+([^|]+?)\\s+sec\\s+([NSEW])`,
        'i'
      )
      const match = text.match(pattern)
      if (!match) return null
      return {
        deg: match[1].trim() === '--' ? '' : match[1].trim(),
        min: match[2].trim() === '--' ? '' : match[2].trim(),
        sec: match[3].trim() === '--' ? '' : match[3].trim(),
        dir: match[4].toUpperCase(),
      }
    }

    const lat = extract('Latitude') ?? extract('Start Lat')
    const lon = extract('Longitude') ?? extract('Start Lon')
    const stopLat = extract('Stop Lat')
    const stopLon = extract('Stop Lon')

    return {
      startLatDeg: lat?.deg ?? defaults.startLatDeg,
      startLatMin: lat?.min ?? defaults.startLatMin,
      startLatSec: lat?.sec ?? defaults.startLatSec,
      startLatDir: (lat?.dir as 'N' | 'S') ?? defaults.startLatDir,
      startLonDeg: lon?.deg ?? defaults.startLonDeg,
      startLonMin: lon?.min ?? defaults.startLonMin,
      startLonSec: lon?.sec ?? defaults.startLonSec,
      startLonDir: (lon?.dir as 'E' | 'W') ?? defaults.startLonDir,
      stopLatDeg: stopLat?.deg ?? defaults.stopLatDeg,
      stopLatMin: stopLat?.min ?? defaults.stopLatMin,
      stopLatSec: stopLat?.sec ?? defaults.stopLatSec,
      stopLatDir: (stopLat?.dir as 'N' | 'S') ?? defaults.stopLatDir,
      stopLonDeg: stopLon?.deg ?? defaults.stopLonDeg,
      stopLonMin: stopLon?.min ?? defaults.stopLonMin,
      stopLonSec: stopLon?.sec ?? defaults.stopLonSec,
      stopLonDir: (stopLon?.dir as 'E' | 'W') ?? defaults.stopLonDir,
    }
  }

  const buildPositionValue = (fields: DprPositionFields): string => {
    const visibleValues = [
      fields.startLatDeg,
      fields.startLatMin,
      fields.startLatSec,
      fields.startLonDeg,
      fields.startLonMin,
      fields.startLonSec,
    ].map((value) => String(value).trim())
    const hasAnyValue = visibleValues.some((value) => value !== '')
    if (!hasAnyValue) return ''

    const formatCoord = (label: string, deg: string, min: string, sec: string, dir: string) =>
      `${label}: ${deg.trim() || '--'} Deg ${min.trim() || '--'} min ${sec.trim() || '--'} sec ${dir}`

    return [
      formatCoord('Latitude', fields.startLatDeg, fields.startLatMin, fields.startLatSec, fields.startLatDir),
      formatCoord('Longitude', fields.startLonDeg, fields.startLonMin, fields.startLonSec, fields.startLonDir),
    ].join(' | ')
  }


  /* ───────────── Tab definitions ───────────── */
  const TABS = [
    'general',
    'foConsumption',
    'activity',
    'activitySummary',
    'robStatus',
    'weather',
    'downtime',
    'pob',
    'statisticsSafety',
    'passengerDetails',
    'engineRunningHours',
    'engineParam',
    'exhTemp',
  ] as const
  type TabKey = (typeof TABS)[number]

  const TAB_LABELS: Record<TabKey, string> = {
    general: 'Ships Location',
    foConsumption: 'FO Consumption',
    activity: 'Activity',
    activitySummary: 'Activity Summary',
    robStatus: 'ROB Status',
    weather: 'Weather',
    downtime: 'Downtime',
    pob: 'POB',
    statisticsSafety: 'Statistics / Safety',
    passengerDetails: 'Passenger List',
    engineRunningHours: 'Engine Running Hours',
    engineParam: 'Engine Parameters',
    exhTemp: 'Exhaust Temps',
  }

  const MACHINERY_OPTIONS = ['ME 1', 'ME 2', 'AE 1', 'AE 2', 'AE 3', 'AE 4', 'Boiler-1', 'Boiler-2', 'Misc'] as const
  const LUBE_OIL_GRADE_OPTIONS = [
    'DISOLA M 4015/Gadinia s3 40/ MAK 401',
    '15W40',
    'SHELL CORENA S2P 100/ HYCOM C-100',
    'MOBIL SHC 629 / MAX SYNGEAR 150',
    'MAK AMOCAM 68/OMALA S2G 68/ MOBIL GEAR 600 XP-68',
    'AWHM-100',
  ] as const
  const ROB_NEGATIVE_TOAST_ID = 'rob-negative-closing-stock'
  const ROB_FIRST_DPR_TOAST_ID = 'rob-first-dpr-opening-stock'
  const LUBE_NEGATIVE_TOAST_ID = 'lube-negative-closing-stock'
  const normalizeMachineryValue = (value: string) => value.trim().replace(/\s+/g, ' ')
  const toNumberOrZero = (value: string) => {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  const isZeroLikeInputValue = (value: string) => {
    const normalized = value.trim()
    return ['0', '0.0', '0.00', '0.000', '0.0000'].includes(normalized)
  }
  const formatCappedRobValue = (value: number) => Number(value.toFixed(2)).toString()
  const displayLubeValue = (val: string) => (String(val ?? '').trim() === '' ? '0.000' : val)
  const computeSafetyCumulative = (previousDay: string, today: string) => {
    if (previousDay.trim() === '' && today.trim() === '') return ''
    return (toNumberOrZero(previousDay) + toNumberOrZero(today)).toString()
  }
  const isFuelCommodity = (commodity: string) => commodity.trim().toLowerCase().includes('fuel')
  const getMachineryOptionsForRow = (
    rows: Array<{machinery: string}>,
    rowIndex: number
  ) => {
    return getSelectableOptionsForRow(rows, rowIndex, MACHINERY_OPTIONS)
  }

  const getLubeGradeOptionsForRow = (
    rows: Array<{machinery: string}>,
    rowIndex: number
  ) => {
    return getSelectableOptionsForRow(rows, rowIndex, LUBE_OIL_GRADE_OPTIONS)
  }

  const getSelectableOptionsForRow = (
    rows: Array<{machinery: string}>,
    rowIndex: number,
    options: readonly string[]
  ) => {
    const normalizedSelected = normalizeMachineryValue(rows[rowIndex]?.machinery ?? '')
    const selectedByOtherRows = new Set(
      rows
        .filter((_, idx) => idx !== rowIndex)
        .map((row) => normalizeMachineryValue(row.machinery))
        .filter((value) => value !== '')
    )

    const availableOptions = options.filter((option) => !selectedByOtherRows.has(option))

    if (
      normalizedSelected &&
      !availableOptions.includes(normalizedSelected)
    ) {
      return [normalizedSelected, ...availableOptions]
    }

    return availableOptions
  }

  /* ───────────── Props ───────────── */
  interface AddDprModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: (dpr: DailyPositionReportRecord) => void | Promise<void>
    editData?: DailyPositionReportRecord | null
    previousDpr?: DailyPositionReportRecord | null
    selectedVesselId?: number
    selectedVesselName?: string
    vesselOptions?: Array<{id: number; name: string}>
  }

  /* ===================================================================== */
  export 

const resolveRobClosingStock = (row: any) => {
  const explicitClosing = row.closingStock ?? row.closing
  if (explicitClosing !== undefined && explicitClosing !== null && explicitClosing !== '' && explicitClosing !== '-') return explicitClosing

  const openingStock = parseFloat(row.openingStock)
  if (!isNaN(openingStock)) {
    const received = parseFloat(row.received) || 0
    const deliveredVessel = parseFloat(row.deliveredToOtherVessel) || 0
    const deliveredPlatforms = parseFloat(row.deliveredToPlatforms) || 0
    const consumed = parseFloat(row.consumedByVessel) || 0
    return (openingStock + received - deliveredVessel - deliveredPlatforms - consumed).toFixed(3).replace(/\.?0+$/, '')
  }
  return ''
}

const resolveLubeClosingStock = (row: any) => {
  const explicitClosing = row.closingStock ?? row.closing
  if (explicitClosing !== undefined && explicitClosing !== null && explicitClosing !== '' && explicitClosing !== '-') return explicitClosing

  const openingStock = parseFloat(row.openingStock)
  if (!isNaN(openingStock)) {
    const received = parseFloat(row.received) || 0
    const consumed = parseFloat(row.consumedByVessel) || 0
    return (openingStock + received - consumed).toFixed(3).replace(/\.?0+$/, '')
  }
  return ''
}

const AddDprModal: React.FC<AddDprModalProps> = ({
    isOpen,
    onClose,
    onSave,
    editData,
    previousDpr,
    selectedVesselId,
    selectedVesselName,
    vesselOptions = [],
  }) => {
    const { currentUser } = useAuth()
    const [activeTab, setActiveTab] = useState<TabKey>('foConsumption')
    const [isSaving, setIsSaving] = useState(false)
    const [showHistoryDrawer, setShowHistoryDrawer] = useState(false)
    const saveInFlightRef = useRef(false)
    const selectedVesselIdNum = Number(selectedVesselId ?? 0)
    const currentUserVesselId = Number(currentUser?.vessel?.id ?? 0)
    const reportId = editData?.id

    const handleRemoteSectionUpdate = useCallback(
      (section: string, payload: any, updatedBy: string) => {
        toast.info(`${updatedBy} updated ${section} in real-time.`, {
          position: 'bottom-right',
          autoClose: 3000,
        })
        if (section === 'HEADER' || section === 'NAVIGATION') {
          if (activeTab !== 'general') {
            if (payload.reportDate) setReportDate(payload.reportDate)
            if (payload.vesselName) setVesselName(payload.vesselName)
            if (payload.voyageNo) setVoyageNo(payload.voyageNo)
            if (payload.fromLocation) setFromLocation(payload.fromLocation)
            if (payload.toLocation) setToLocation(payload.toLocation)
            if (payload.eta) setEta(payload.eta)
            if (payload.weather) setWeather(payload.weather)
          }
        } else if (section === 'FO_CONSUMPTION' && activeTab !== 'foConsumption') {
          if (payload.foConsumptionRows?.length) setFoConsumptionRows(payload.foConsumptionRows)
          if (payload.machineryFoConsumptionRows?.length) setMachineryFoConsumptionRows(payload.machineryFoConsumptionRows)
        } else if (section === 'ACTIVITIES' && activeTab !== 'activity') {
          if (payload.activities?.length) setActivities(payload.activities)
        } else if (section === 'ROB' && activeTab !== 'robStatus') {
          if (payload.robStatus?.length) setRobRows(payload.robStatus)
        } else if (section === 'WEATHER_ROWS' && activeTab !== 'weather') {
          if (payload.weatherRows?.length) setWeatherRows(payload.weatherRows)
          if (payload.tides) setTides(payload.tides)
        } else if (section === 'DOWNTIME' && activeTab !== 'downtime') {
          if (payload.downtimeRows?.length) setDowntimeRows(payload.downtimeRows)
        } else if (section === 'POB' && activeTab !== 'pob') {
          if (payload.pobRows?.length) setPobRows(payload.pobRows)
        }
      },
      [activeTab]
    )

    const realtime = useDprRealtime(reportId, handleRemoteSectionUpdate)
    const autoSave = useDprAutoSave(reportId)

    const TAB_TO_SECTION: Record<TabKey, string> = {
      general: 'NAVIGATION',
      foConsumption: 'FO_CONSUMPTION',
      activity: 'ACTIVITIES',
      activitySummary: 'ACTIVITY_SUMMARY',
      robStatus: 'ROB',
      weather: 'WEATHER_ROWS',
      downtime: 'DOWNTIME',
      pob: 'POB',
      statisticsSafety: 'SAFETY',
      passengerDetails: 'PASSENGERS',
      engineRunningHours: 'RUNNING_HOURS',
      engineParam: 'ENGINE_PARAMS',
      exhTemp: 'EXH_TEMP',
    }

    const resolveDefaultVesselId = () => {
      if (editData?.vesselId && Number(editData.vesselId) > 0) return Number(editData.vesselId)
      if (selectedVesselIdNum > 0) return selectedVesselIdNum
      if (currentUserVesselId > 0) return currentUserVesselId
      return 0
    }

    const resolveDefaultVesselName = () =>
      editData?.vesselName
      ?? selectedVesselName
      ?? currentUser?.vessel?.fleet_name
      ?? ''

    const loadDefaultVoyageNo = async (vesselId: number, vesselNameHint?: string) => {
      const voyages = vesselId > 0 ? await getVoyagesByVessel(vesselId) : await getVoyageList()
      const filteredVoyages = vesselId > 0
        ? voyages
        : (vesselNameHint
            ? voyages.filter((v: any) => String(v?.vessel?.fleet_name ?? '') === vesselNameHint)
            : voyages)
      const activeVoyage = filteredVoyages.find((v: any) => v.active)
      return activeVoyage?.voyageNumber ?? (filteredVoyages.length > 0 ? filteredVoyages[filteredVoyages.length - 1]?.voyageNumber ?? '' : '')
    }

    // ── Tab 0: FO Consumption
    const [foConsumptionRows, setFoConsumptionRows] = useState<DprFoConsumptionRow[]>(
      editData?.foConsumptionRows?.length ? editData.foConsumptionRows : [blankFoConsumptionRow()]
    )
    const [foConsumptionTouched, setFoConsumptionTouched] = useState(false)
    const [machineryFoConsumptionRows, setMachineryFoConsumptionRows] = useState<DprMachineryFoConsumptionRow[]>(
      editData?.machineryFoConsumptionRows?.length
        ? editData.machineryFoConsumptionRows
        : [blankMachineryFoConsumptionRow()]
    )

    // ── Tab 1: General Info ──
    const [reportDate, setReportDate] = useState(editData?.reportDate ?? '')
    // ── Local state for selected vessel, so user can pick if not pre-provided
    const [vesselId, setVesselId] = useState<number>(() => resolveDefaultVesselId())
    const [vesselName, setVesselName] = useState(editData?.vesselName ?? '')
    const [voyageNo, setVoyageNo] = useState(editData?.voyageNo ?? '')
    const [portOrSea, setPortOrSea] = useState(editData?.portOrSea ?? 'SEA')
    const [fromLocation, setFromLocation] = useState(editData?.fromLocation ?? '')
    const [toLocation, setToLocation] = useState(editData?.toLocation ?? '')
    const [eta, setEta] = useState(editData?.eta ?? '')
    const [draftFwd, setDraftFwd] = useState(editData?.draftFwd ?? '')
    const [draftAft, setDraftAft] = useState(editData?.draftAft ?? '')
    const [weather, setWeather] = useState(editData?.weather ?? '')
    const [wind, setWind] = useState(editData?.wind ?? '')
    const [seaState, setSeaState] = useState(editData?.seaState ?? '')

    // Date & Location
    const [dlDate, setDlDate] = useState(editData?.dlDate ?? new Date().toISOString().split('T')[0])
    const [dlTime, setDlTime] = useState(editData?.dlTime ?? '')
    const [dlLocation, setDlLocation] = useState(editData?.dlLocation ?? '')
    const [dlPosition, setDlPosition] = useState(editData?.dlPosition ?? '')
    const [positionFields, setPositionFields] = useState<DprPositionFields>(
      parsePositionValue(editData?.dlPosition)
    )
    const [positionErrors, setPositionErrors] = useState<DprPositionErrors>({})

    // ── Tab 2: Activity rows ──
    const [activities, setActivities] = useState<DprActivity[]>(
      editData?.activities?.length ? editData.activities : [blankActivity()]
    )

    // ── Tab 3: Activity Summary ──
    const initSummary = editData?.activitySummary ?? blankSummary()
    const [speedsUpto6, setSpeedsUpto6] = useState(initSummary.speedsUpto6)
    const [speedsAbove6, setSpeedsAbove6] = useState(initSummary.speedsAbove6)
    const [inDpMode, setInDpMode] = useState(initSummary.inDpMode)
    const [atAnchorageBerth, setAtAnchorageBerth] = useState(initSummary.atAnchorageBerth)
    const [downTimeSummary, setDownTimeSummary] = useState(initSummary.downTime)
    const [othersSpecify, setOthersSpecify] = useState(initSummary.othersSpecify)
    const [othersLabel, setOthersLabel] = useState(initSummary.othersLabel)
    const [total, setTotal] = useState(initSummary.total)
    const [fuelConsumedIn24Hrs, setFuelConsumedIn24Hrs] = useState(initSummary.fuelConsumedIn24Hrs)

    // ── Tab 4: ROB Status ──
    const parseNumber = (val: string | number | undefined) => {
      const num = typeof val === 'number' ? val : parseFloat(val ?? '0')
      return isNaN(num) ? 0 : num
    }

    const hasPreviousRobStatus = Boolean(previousDpr?.robStatus?.length)
    const requiresInitialRobOpening = !editData && !hasPreviousRobStatus

    const makeRobDefaults = (prevRob: DprRobRow[] | null | undefined): DprRobRow[] => {
      if (!prevRob?.length) {
        return DEFAULT_ROB_ROWS.map((r) => ({
          ...r,
          openingStock: requiresInitialRobOpening ? '' : r.openingStock,
          closingStock: requiresInitialRobOpening ? '' : r.closingStock,
        }))
      }
      return prevRob.map((prev) => {
        const closingStock = parseNumber(prev.closingStock)
        return {
          ...prev,
          openingStock: closingStock.toFixed(2),
          received: '0',
          consumedByVessel: '0',
          deliveredToOtherVessel: '0',
          deliveredToPlatforms: '0',
          closingStock: '',
        }
      })
    }

    const makeLubeOilDefaults = (prevRows: DprLubeOilStatusRow[] | null | undefined): DprLubeOilStatusRow[] => {
      if (!prevRows?.length) return [blankLubeOilStatusRow()]
      return prevRows.map((prev) => {
        const resolvedClosing = resolveLubeClosingStock(prev) || ''
        return {
          machinery: prev.machinery,
          openingStock: resolvedClosing,
          received: '0',
          consumedByVessel: '0',
          closingStock: resolvedClosing,
        }
      })
    }

    const makeSafetyDefaults = (
      defaultRows: DprSafetyRow[],
      prevRows: DprSafetyRow[] | null | undefined
    ): DprSafetyRow[] => {
      const previousByParticular = new Map(
        (prevRows ?? []).map((row) => [row.particulars.trim().toLowerCase(), row])
      )

      return defaultRows.map((row) => {
        const matchedPrevious = previousByParticular.get(row.particulars.trim().toLowerCase())
        const previousDay = String(matchedPrevious?.cumulative ?? row.previousDay ?? '0')
        const today = '0'

        return {
          ...row,
          previousDay,
          today,
          cumulative: computeSafetyCumulative(previousDay, today),
        }
      })
    }

    const [robRows, setRobRows] = useState<DprRobRow[]>(() => {
      if (editData?.robStatus?.length) {
        return editData.robStatus
      }
      if (previousDpr?.robStatus?.length) {
        return previousDpr.robStatus.map(r => {
          const resolvedClosing = resolveRobClosingStock(r) || ''
          return {
            commodity: r.commodity,
            openingStock: resolvedClosing,
            received: '0',
            deliveredToOtherVessel: '0',
            deliveredToPlatforms: '0',
            consumedByVessel: '0',
            closingStock: resolvedClosing
          }
        })
      }
      return makeRobDefaults(previousDpr?.robStatus)
    })
    const [lubeOilStatusRows, setLubeOilStatusRows] = useState<DprLubeOilStatusRow[]>(
      editData?.lubeOilStatusRows?.length ? editData.lubeOilStatusRows : makeLubeOilDefaults(previousDpr?.lubeOilStatusRows)
    )
    const [customLubeOilRows, setCustomLubeOilRows] = useState<Record<number, boolean>>(() => {
      const initialRows = editData?.lubeOilStatusRows?.length
        ? editData.lubeOilStatusRows
        : makeLubeOilDefaults(previousDpr?.lubeOilStatusRows)
      const mapping: Record<number, boolean> = {}
      initialRows.forEach((row, idx) => {
        const val = row.machinery || ''
        if (val && !LUBE_OIL_GRADE_OPTIONS.includes(val as any)) {
          mapping[idx] = true
        }
      })
      return mapping
    })

    // ── Tab 5: Weather ──
    const [weatherRows, setWeatherRows] = useState<DprWeatherRow[]>(
      editData?.weatherRows?.length ? editData.weatherRows : DEFAULT_WEATHER_ROWS.map((r) => ({ ...r }))
    )
    const [tides, setTides] = useState<DprTides>(editData?.tides ?? blankTides())

    // ── Tab 6: Downtime ──
    const [downtimeRows, setDowntimeRows] = useState<DprDowntimeRow[]>(
      editData?.downtimeRows?.length ? editData.downtimeRows : [blankDowntimeRow()]
    )

    // ── Tab 7: P.O.B ──
    const [pobRows, setPobRows] = useState<DprPobRow[]>(
      editData?.pobRows?.length ? editData.pobRows : DEFAULT_POB_ROWS.map((r) => ({ ...r }))
    )

    // ── Tab 8: Statistics & Safety ──
    const [safetyLeft, setSafetyLeft] = useState<DprSafetyRow[]>(
      editData?.safetyLeft?.length
        ? editData.safetyLeft
        : makeSafetyDefaults(DEFAULT_SAFETY_LEFT, previousDpr?.safetyLeft)
    )
    const [safetyRight, setSafetyRight] = useState<DprSafetyRow[]>(
      editData?.safetyRight?.length
        ? editData.safetyRight
        : makeSafetyDefaults(DEFAULT_SAFETY_RIGHT, previousDpr?.safetyRight)
    )
    const [equipmentRows, setEquipmentRows] = useState<DprEquipmentRow[]>(
      normalizeEquipmentRows(editData?.equipmentRows)
    )

    // ── Tab 9:  PASSENGER List ──
    const [passengerRows, setPassengerRows] = useState<DprPassengerRow[]>(
      editData?.passengerRows?.length ? normalizePassengerRows(editData.passengerRows) : normalizePassengerRows([blankPassengerRow()])
    )

    // ── Tab 10: Engine Running Hours
    const [engineRunningHoursRows, setEngineRunningHoursRows] = useState<DprEngineRunningHoursRow[]>(
      editData?.engineRunningHours?.length
        ? editData.engineRunningHours
        : (previousDpr?.engineRunningHours?.length
            ? previousDpr.engineRunningHours.map(r => ({
                label: r.label,
                openingHours: r.totalHours,
                dailyHours: '0',
                totalHours: r.totalHours
              }))
            : DEFAULT_ENGINE_RUNNING_HOURS)
    )
            const [engineRunningHoursErrors, setEngineRunningHoursErrors] = useState<Record<string, string>>({})

    const [engineParamRows, setEngineParamRows] = useState<DprEngineParamRow[]>(
      editData?.engineTemp?.params?.length
        ? editData.engineTemp.params
        : DEFAULT_ENGINE_PARAMS.map((r) => ({...r}))
    )
    const [exhTempRows, setExhTempRows] = useState<DprExhTempRow[]>(
      editData?.engineTemp?.exhTemps?.length
        ? editData.engineTemp.exhTemps
        : DEFAULT_EXH_TEMPS.map((r) => ({...r}))
    )

    /* ── Auto-reset state when modal opens ── */
    React.useEffect(() => {
      if (isOpen) {
        const defaultVesselId = resolveDefaultVesselId()
        const defaultVesselName = resolveDefaultVesselName()

        setActiveTab('general')
        setFoConsumptionRows(editData?.foConsumptionRows?.length ? editData.foConsumptionRows : [blankFoConsumptionRow()])
        setFoConsumptionTouched(false)
        setMachineryFoConsumptionRows(
          editData?.machineryFoConsumptionRows?.length
            ? editData.machineryFoConsumptionRows
            : [blankMachineryFoConsumptionRow()]
        )
        setReportDate(editData?.reportDate ?? new Date().toISOString().split('T')[0])
        setVesselId(defaultVesselId)
        setVesselName(defaultVesselName || '')
        
        if (editData?.voyageNo) {
          setVoyageNo(editData.voyageNo)
        } else {
          setVoyageNo('Loading...')
          loadDefaultVoyageNo(defaultVesselId, defaultVesselName)
            .then((resolvedVoyageNo) => {
              setVoyageNo(resolvedVoyageNo ?? '')
            })
            .catch(err => {
              console.error("Failed to fetch voyages", err)
              setVoyageNo('')
            })
        }
        setPortOrSea(editData?.portOrSea ?? '')
        setFromLocation(editData?.fromLocation ?? '')
        setToLocation(editData?.toLocation ?? '')
        setEta(editData?.eta ?? '')
        setDraftFwd(editData?.draftFwd ?? '')
        setDraftAft(editData?.draftAft ?? '')
        setWeather(editData?.weather ?? '')
        setWind(editData?.wind ?? '')
        setSeaState(editData?.seaState ?? '')
        setDlDate(editData?.dlDate ?? '')
        setDlTime(editData?.dlTime ?? '')
        setDlLocation(editData?.dlLocation ?? '')
        setDlPosition(editData?.dlPosition ?? '')
        setPositionFields(parsePositionValue(editData?.dlPosition))
        setPositionErrors({})
        setActivities(editData?.activities?.length ? editData.activities : [blankActivity(), blankActivity()])

        const sum = editData?.activitySummary ?? blankSummary()
        setSpeedsUpto6(sum.speedsUpto6)
        setSpeedsAbove6(sum.speedsAbove6)
        setInDpMode(sum.inDpMode)
        setAtAnchorageBerth(sum.atAnchorageBerth)
        setDownTimeSummary(sum.downTime)
        setOthersSpecify(sum.othersSpecify)
        setOthersLabel(sum.othersLabel)
        setTotal(sum.total)
        setFuelConsumedIn24Hrs(sum.fuelConsumedIn24Hrs)

        if (editData?.robStatus?.length) {
          setRobRows(editData.robStatus)
        } else if (previousDpr?.robStatus?.length) {
          setRobRows(previousDpr.robStatus.map(r => {
            const resolvedClosing = resolveRobClosingStock(r) || ''
            return {
              commodity: r.commodity,
              openingStock: resolvedClosing,
              received: '0',
              deliveredToOtherVessel: '0',
              deliveredToPlatforms: '0',
              consumedByVessel: '0',
              closingStock: resolvedClosing
            }
          }))
        } else {
          setRobRows(makeRobDefaults(previousDpr?.robStatus))
        }
        setLubeOilStatusRows(
          editData?.lubeOilStatusRows?.length
            ? editData.lubeOilStatusRows
            : makeLubeOilDefaults(previousDpr?.lubeOilStatusRows)
        )
        const initialLubeRows = editData?.lubeOilStatusRows?.length
          ? editData.lubeOilStatusRows
          : makeLubeOilDefaults(previousDpr?.lubeOilStatusRows)
        const mapping: Record<number, boolean> = {}
        initialLubeRows.forEach((row, idx) => {
          const val = row.machinery || ''
          if (val && !LUBE_OIL_GRADE_OPTIONS.includes(val as any)) {
            mapping[idx] = true
          }
        })
        setCustomLubeOilRows(mapping)

        setWeatherRows(editData?.weatherRows?.length ? editData.weatherRows : DEFAULT_WEATHER_ROWS.map((r) => ({ ...r })))
        setTides(editData?.tides ?? blankTides())
        setDowntimeRows(editData?.downtimeRows?.length ? editData.downtimeRows : [blankDowntimeRow()])
        setPobRows(editData?.pobRows?.length ? editData.pobRows : DEFAULT_POB_ROWS.map((r) => ({ ...r })))
        setSafetyLeft(
          editData?.safetyLeft?.length
            ? editData.safetyLeft
            : makeSafetyDefaults(DEFAULT_SAFETY_LEFT, previousDpr?.safetyLeft)
        )
        setSafetyRight(
          editData?.safetyRight?.length
            ? editData.safetyRight
            : makeSafetyDefaults(DEFAULT_SAFETY_RIGHT, previousDpr?.safetyRight)
        )
        setEquipmentRows(normalizeEquipmentRows(editData?.equipmentRows))
        setPassengerRows(
          editData?.passengerRows?.length
            ? normalizePassengerRows(editData.passengerRows)
            : normalizePassengerRows([blankPassengerRow()])
        )
        setEngineRunningHoursRows(
          editData?.engineRunningHours?.length
            ? editData.engineRunningHours
            : (previousDpr?.engineRunningHours?.length
                ? previousDpr.engineRunningHours.map(r => ({
                    label: r.label,
                    openingHours: r.totalHours,
                    dailyHours: '0',
                    totalHours: r.totalHours
                  }))
                : DEFAULT_ENGINE_RUNNING_HOURS)
        )
        setEngineParamRows(
          editData?.engineTemp?.params?.length
            ? editData.engineTemp.params
            : DEFAULT_ENGINE_PARAMS.map((r) => ({...r}))
        )
        setExhTempRows(
          editData?.engineTemp?.exhTemps?.length
            ? editData.engineTemp.exhTemps
            : DEFAULT_EXH_TEMPS.map((r) => ({...r}))
        )
        setEngineRunningHoursErrors({})
      } else {
        // New / Reset
        setFoConsumptionRows([blankFoConsumptionRow()])
        setMachineryFoConsumptionRows([blankMachineryFoConsumptionRow()])
        setReportDate(new Date().toISOString().split('T')[0])
        setVesselId(resolveDefaultVesselId())
        setVesselName(resolveDefaultVesselName() || '')
        setVoyageNo('')
        setPortOrSea('SEA')
        setFromLocation('')
        setToLocation('')
        setEta('')
        setDraftFwd('')
        setDraftAft('')
        setWeather('')
        setWind('')
        setSeaState('')
        setDlDate(new Date().toISOString().split('T')[0])
        setDlTime('')
        setDlLocation('')
        setDlPosition('')
        setPositionFields(blankPositionFields())
        setPositionErrors({})
        setActivities([blankActivity()])
        setSpeedsUpto6('')
        setSpeedsAbove6('')
        setInDpMode('')
        setAtAnchorageBerth('')
        setDownTimeSummary('')
        setOthersSpecify('')
        setOthersLabel('')
        setTotal('')
        setFuelConsumedIn24Hrs('')
        setRobRows(makeRobDefaults(previousDpr?.robStatus))
        setLubeOilStatusRows(makeLubeOilDefaults(previousDpr?.lubeOilStatusRows))
        setCustomLubeOilRows({})
        setWeatherRows(DEFAULT_WEATHER_ROWS.map((r) => ({ ...r })))
        setTides(blankTides())
        setDowntimeRows([blankDowntimeRow()])
        setPobRows(DEFAULT_POB_ROWS.map((r) => ({ ...r })))
        setSafetyLeft(makeSafetyDefaults(DEFAULT_SAFETY_LEFT, previousDpr?.safetyLeft))
        setSafetyRight(makeSafetyDefaults(DEFAULT_SAFETY_RIGHT, previousDpr?.safetyRight))
        setEquipmentRows(normalizeEquipmentRows())
        setPassengerRows(normalizePassengerRows([blankPassengerRow()]))
        setEngineRunningHoursRows(DEFAULT_ENGINE_RUNNING_HOURS.map((r) => ({ ...r })))
        setEngineParamRows(DEFAULT_ENGINE_PARAMS.map((r) => ({...r})))
        setExhTempRows(DEFAULT_EXH_TEMPS.map((r) => ({...r})))
        setEngineRunningHoursErrors({})
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, editData, previousDpr, selectedVesselIdNum, selectedVesselName, currentUser?.vessel?.id, currentUser?.vessel?.fleet_name])

    // ── Tab 3: Activity Summary Automation ──
    useEffect(() => {
      const sumVal = (fn: (r: DprFoConsumptionRow) => string) => {
        return foConsumptionRows.reduce((acc, r) => acc + (parseFloat(fn(r)) || 0), 0)
      }

      const sAbove6 = sumVal((r) => r.above6Hrs)
      const sUpto6 = sumVal((r) => r.steamingHrs)
      const sInDp = sumVal((r) => r.platformDpHrs)
      const sAtAnchBerth = sumVal((r) => r.atAnchorageHrs) + sumVal((r) => r.atBerthHrs)
      const sTotal = sumVal((r) => r.totalHr1)
      const machineryTotal = getValidMachineryFoRows().reduce(
        (acc, row) => acc + (parseFloat(row.foConsumption) || 0),
        0
      )
      const hasMachineryFuel = getValidMachineryFoRows().length > 0
      const sFuelCons = hasMachineryFuel ? machineryTotal : sumVal((r) => r.totalFoCons)

      setSpeedsAbove6(sAbove6.toFixed(1))
      setSpeedsUpto6(sUpto6.toFixed(1))
      setInDpMode(sInDp.toFixed(1))
      setAtAnchorageBerth(sAtAnchBerth.toFixed(1))
      setTotal(sTotal.toFixed(1))
      const syncedFuelConsumed = sFuelCons.toFixed(3)
      setFuelConsumedIn24Hrs(syncedFuelConsumed)
      setRobRows((prev) =>
        prev.map((row) => {
          if (!isFuelCommodity(row.commodity)) return row

          const hasOp = row.openingStock !== undefined && row.openingStock !== null && row.openingStock !== ''
          const opening = toNumberOrZero(row.openingStock)
          const received = toNumberOrZero(row.received)
          const deliveredToOtherVessel = toNumberOrZero(row.deliveredToOtherVessel)
          const deliveredToPlatforms = toNumberOrZero(row.deliveredToPlatforms)
          const consumedByVessel = toNumberOrZero(syncedFuelConsumed)
          const computedClosingStock = opening + received - deliveredToOtherVessel - deliveredToPlatforms - consumedByVessel

          return {
            ...row,
            consumedByVessel: syncedFuelConsumed,
            closingStock: (!hasOp && !row.received && !row.consumedByVessel && !row.deliveredToOtherVessel && !row.deliveredToPlatforms) ? '' : Math.max(0, computedClosingStock).toFixed(2),
          }
        })
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [foConsumptionRows, machineryFoConsumptionRows])

    const isValidNumericInput = (value: string) => /^\d*\.?\d*$/.test(value)

    const isValidMachineryOption = (value: string) => MACHINERY_OPTIONS.includes(value as (typeof MACHINERY_OPTIONS)[number])

    const getValidMachineryFoRows = () =>
      machineryFoConsumptionRows
        .filter((row) => isValidMachineryOption(row.machinery) && row.foConsumption.trim() !== '')
        .filter((row) => !Number.isNaN(parseFloat(row.foConsumption)))
        .map((row) => ({ ...row, foConsumption: row.foConsumption.trim() }))

    const hasAnyMachineryFoEntry = () =>
      machineryFoConsumptionRows.some((row) => row.machinery !== '' || row.foConsumption.trim() !== '')

    const hasAnyExistingFoEntry = () =>
      foConsumptionRows.some((row) => {
        return [
          row.above6Hrs,
          row.above6Cons,
          row.steamingHrs,
          row.steamingCons,
          row.platformDpHrs,
          row.platformDpCons,
          row.atAnchorageHrs,
          row.atAnchorageCons,
          row.atBerthHrs,
          row.atBerthCons,
        ].some((value) => String(value ?? '').trim() !== '')
      })

    const machineryFoConsumptionTotal = getValidMachineryFoRows().reduce(
      (acc, row) => acc + (parseFloat(row.foConsumption) || 0),
      0
    )
    const hasValidMachineryFoRows = getValidMachineryFoRows().length > 0

    const handleMachineryFoConsumptionChange = (
      index: number,
      field: keyof DprMachineryFoConsumptionRow,
      value: string
    ) => {
      if (field === 'foConsumption' && value !== '' && !isValidNumericInput(value)) {
        return
      }

      const nextValue = field === 'machinery' ? normalizeMachineryValue(value) : value

      setMachineryFoConsumptionRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: nextValue }
        return copy
      })
    }

    const addMachineryFoConsumptionRow = () => {
      setMachineryFoConsumptionRows((prev) => [...prev, blankMachineryFoConsumptionRow()])
    }

    const removeMachineryFoConsumptionRow = (index: number) => {
      setMachineryFoConsumptionRows((prev) => {
        const next = prev.filter((_, idx) => idx !== index)
        return next.length ? next : [blankMachineryFoConsumptionRow()]
      })
    }

    /* ── Activity helpers ── */
    const handleActivityChange = (index: number, field: keyof DprActivity, value: string) => {
      setActivities((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: value }  
        return copy
      })
    }
    const addActivityRow = () =>
      setActivities((prev) => [...prev, blankActivity()])
    const removeActivityRow = (index: number) =>
      setActivities((prev) => prev.filter((_, i) => i !== index))

    /* ── ROB helpers ── */
    const handleRobChange = (index: number, field: keyof DprRobRow, value: string) => {
      setRobRows((prev) => {
        const copy = [...prev]
        const updatedRow = { ...copy[index], [field]: value }

        // Auto-calculate Closing Stock if variables change
        if (['openingStock', 'received', 'consumedByVessel', 'deliveredToOtherVessel', 'deliveredToPlatforms'].includes(field as string)) {
          const parse = (val: string) => {
            const num = parseFloat(val)
            return isNaN(num) ? 0 : num
          }
          const hasOp = updatedRow.openingStock !== undefined && updatedRow.openingStock !== null && updatedRow.openingStock !== ''
          
          const opening = parse(updatedRow.openingStock)
          const received = parse(updatedRow.received)
          const consumedByVessel = parse(updatedRow.consumedByVessel)
          let deliveredToOtherVessel = parse(updatedRow.deliveredToOtherVessel)
          let deliveredToPlatforms = parse(updatedRow.deliveredToPlatforms)

          // Prevent entering delivered amounts greater than available stock
          const availableForDelivery = opening + received - consumedByVessel
          const existingOther = parse(updatedRow.deliveredToOtherVessel)
          const existingPlatforms = parse(updatedRow.deliveredToPlatforms)

          if (field === 'deliveredToOtherVessel') {
            const newVal = parse(value)
            const maxAllowed = Math.max(0, availableForDelivery - existingPlatforms)
            if (newVal > maxAllowed) {
              toast.warn('Delivered to other Vessel cannot exceed available stock.', { position: 'top-center' })
              deliveredToOtherVessel = maxAllowed
              updatedRow.deliveredToOtherVessel = formatCappedRobValue(maxAllowed)
            } else {
              deliveredToOtherVessel = newVal
            }
          }

          if (field === 'deliveredToPlatforms') {
            const newVal = parse(value)
            const maxAllowed = Math.max(0, availableForDelivery - existingOther)
            if (newVal > maxAllowed) {
              toast.warn('Delivered to Platforms cannot exceed available stock.', { position: 'top-center' })
              deliveredToPlatforms = maxAllowed
              updatedRow.deliveredToPlatforms = formatCappedRobValue(maxAllowed)
            } else {
              deliveredToPlatforms = newVal
            }
          }
          
          const computedClosingStock = opening + received - consumedByVessel - deliveredToOtherVessel - deliveredToPlatforms
          if (!hasOp && !updatedRow.received && !updatedRow.consumedByVessel && !updatedRow.deliveredToOtherVessel && !updatedRow.deliveredToPlatforms) {
            updatedRow.closingStock = ''
          } else if (computedClosingStock < 0) {
            updatedRow.closingStock = ''
          } else {
            updatedRow.closingStock = computedClosingStock.toFixed(2)
          }
        }

        if (field === 'closingStock') {
          const manualClosingStock = parseFloat(updatedRow.closingStock)
          if (!Number.isNaN(manualClosingStock) && manualClosingStock < 0) {
            toast.warn('Closing stock cannot be negative. Please adjust ROB values.', {
              position: 'top-center',
              toastId: ROB_NEGATIVE_TOAST_ID,
            })
            updatedRow.closingStock = '0.00'
          }
        }

        copy[index] = updatedRow
        return copy
      })
    }

    /* ── Lube Oil Status helpers ── */
    const handleLubeOilStatusChange = (
      index: number,
      field: keyof DprLubeOilStatusRow,
      value: string
    ) => {
      if (field !== 'machinery' && value !== '' && !isValidNumericInput(value)) {
        return
      }

      const nextValue = field === 'machinery' ? normalizeMachineryValue(value) : value

      setLubeOilStatusRows((prev) => {
        const copy = [...prev]
        const updatedRow = { ...copy[index], [field]: nextValue }

        if (field === 'openingStock' || field === 'received' || field === 'consumedByVessel') {
          const parse = (val: string) => {
            const num = parseFloat(val)
            return Number.isNaN(num) ? 0 : num
          }
          const hasOp = updatedRow.openingStock !== undefined && updatedRow.openingStock !== null && updatedRow.openingStock !== ''

          const opening = parse(updatedRow.openingStock)
          const received = parse(updatedRow.received)
          const consumed = parse(updatedRow.consumedByVessel)
          const computedClosingStock = opening + received - consumed

          if (!hasOp && !updatedRow.received && !updatedRow.consumedByVessel) {
            updatedRow.closingStock = ''
          } else if (computedClosingStock < 0) {
            toast.warn('Lube oil closing stock cannot be negative.', {
              position: 'top-center',
              toastId: LUBE_NEGATIVE_TOAST_ID,
            })
            updatedRow[field] = ''
            updatedRow.closingStock = ''
          } else {
            updatedRow.closingStock = computedClosingStock.toFixed(3)
          }
        }

        copy[index] = updatedRow
        return copy
      })
    }

    const addLubeOilStatusRow = () =>
      setLubeOilStatusRows((prev) => [...prev, blankLubeOilStatusRow()])

    const removeLubeOilStatusRow = (index: number) => {
      setLubeOilStatusRows((prev) => {
        const next = prev.filter((_, i) => i !== index)
        return next.length ? next : [blankLubeOilStatusRow()]
      })
      setCustomLubeOilRows((prev) => {
        const next: Record<number, boolean> = {}
        Object.keys(prev).forEach((keyStr) => {
          const key = Number(keyStr)
          if (key < index) {
            next[key] = prev[key]
          } else if (key > index) {
            next[key - 1] = prev[key]
          }
        })
        return next
      })
    }

    /* ── Weather helpers ── */
    const handleWeatherChange = (index: number, field: keyof DprWeatherRow, value: string) => {
      setWeatherRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: value }
        return copy
      })
    }
    const handleTideChange = (field: keyof DprTides, value: string) => {
      setTides((prev) => ({ ...prev, [field]: value }))
    }

    /* ── Downtime helpers ── */
    const handleDowntimeChange = (index: number, field: keyof DprDowntimeRow, value: string) => {
      setDowntimeRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: value }
        return copy
      })
    }
    const addDowntimeRow = () =>
      setDowntimeRows((prev) => [...prev, blankDowntimeRow()])
    const removeDowntimeRow = (index: number) =>
      setDowntimeRows((prev) => prev.filter((_, i) => i !== index))

    /* ── POB helpers ── */
    const handlePobChange = (index: number, field: keyof DprPobRow, value: string) => {
      setPobRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: value }
        return copy
      })
    }

    /* ── Safety helpers ── */
    const handleSafetyLeftChange = (index: number, field: keyof DprSafetyRow, value: string) => {
      setSafetyLeft((prev) => {
        const copy = [...prev]
        const updatedRow = { ...copy[index], [field]: value }
        if (field === 'previousDay' || field === 'today') {
          updatedRow.cumulative = computeSafetyCumulative(updatedRow.previousDay, updatedRow.today)
        }
        copy[index] = updatedRow
        return copy
      })
    }
    const handleSafetyRightChange = (index: number, field: keyof DprSafetyRow, value: string) => {
      setSafetyRight((prev) => {
        const copy = [...prev]
        const updatedRow = { ...copy[index], [field]: value }
        if (field === 'previousDay' || field === 'today') {
          updatedRow.cumulative = computeSafetyCumulative(updatedRow.previousDay, updatedRow.today)
        }
        copy[index] = updatedRow
        return copy
      })
    }
    const handleEquipmentChange = (index: number, field: keyof DprEquipmentRow, value: string) => {
      setEquipmentRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: value }
        return copy
      })
    }

    const handlePositionFieldChange = (
      field: keyof DprPositionFields,
      value: DprPositionFields[keyof DprPositionFields]
    ) => {
      const nextVal =
        field === 'startLatDir' ||
        field === 'stopLatDir' ||
        field === 'startLonDir' ||
        field === 'stopLonDir'
          ? value
          : sanitizeCoordinatePart(String(value))

      setPositionFields((prev) => ({
        ...prev,
        [field]: nextVal as never,
      }))
      setPositionErrors((prev) => {
        if (!prev[field]) return prev
        const next = {...prev}
        delete next[field]
        return next
      })
    }

    /* ── Passenger helpers ── */
    const handlePassengerChange = (index: number, field: keyof DprPassengerRow, value: string) => {
      setPassengerRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: value }
        return copy
      })
    }
    const addPassengerRow = () =>
      setPassengerRows((prev) => normalizePassengerRows([
        ...prev,
        blankPassengerRow(),
      ]))
    const removePassengerRow = (index: number) =>
      setPassengerRows((prev) => normalizePassengerRows(prev.filter((_, i) => i !== index)))

    /* ── Engine Temp helpers ── */
    const handleEngineParamChange = (index: number, field: keyof DprEngineParamRow, value: string) => {
      setEngineParamRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: value }
        return copy
      })
    }

    const handleExhTempChange = (index: number, field: keyof DprExhTempRow, value: string) => {
      setExhTempRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], [field]: value }
        return copy
      })
    }

    const getExhTempMinValue = (row: DprExhTempRow) => {
      const values = [
        row.unit1,
        row.unit2,
        row.unit3,
        row.unit4,
        row.unit5,
        row.unit6,
        row.unit7,
        row.unit8,
      ]
        .map((value) => String(value ?? '').trim())
        .filter((value) => value !== '')
        .map((value) => parseFloat(value))
        .filter((value) => Number.isFinite(value))

      if (values.length === 0) return ''

      const average = values.reduce((sum, value) => sum + value, 0) / values.length
      return Number(average.toFixed(3)).toString()
    }

    const handleEngineRunningHoursChange = (idx: number, field: keyof DprEngineRunningHoursRow, val: string) => {
      if ((field === 'openingHours' || field === 'dailyHours') && val !== '' && !/^\d*\.?\d*$/.test(val)) {
        return
      }

      const errorKey = `${idx}-${field}`

      setEngineRunningHoursRows((prev) => {
        const updated = [...prev]
        const row = { ...updated[idx], [field]: val }

        if (field === 'openingHours' || field === 'dailyHours') {
          const numericValue = parseFloat(val)
          if (val === '') {
            setEngineRunningHoursErrors((prevErrors) => {
              const next = { ...prevErrors }
              delete next[errorKey]
              return next
            })
          } else if (!Number.isNaN(numericValue) && (numericValue < 0 || numericValue > 25)) {
            setEngineRunningHoursErrors((prevErrors) => ({
              ...prevErrors,
              [errorKey]: 'Please enter a value greater than or equal to 0 and less than 25.',
            }))
            row[field] = ''
          } else {
            setEngineRunningHoursErrors((prevErrors) => {
              const next = { ...prevErrors }
              delete next[errorKey]
              return next
            })
          }
        }
        
        // Auto-calculate Total = Opening + Daily
        if (field === 'openingHours' || field === 'dailyHours') {
          const opening = parseFloat(row.openingHours) || 0
          const daily = parseFloat(row.dailyHours) || 0
          row.totalHours = (opening + daily).toString()
        }
        
        updated[idx] = row
        return updated
      })
    }

    /* ── FO Consumption helpers ── */
    const handleFoConsumptionChange = (index: number, field: keyof DprFoConsumptionRow, value: string) => {
      setFoConsumptionTouched(true)

      setFoConsumptionRows((prev) => {
        const copy = [...prev]
        const updatedRow = { ...copy[index], [field]: value }
        
        // Auto-calculate totals if an hours or consumption field is changed
        if (
          field.includes('Hrs') || 
          field.includes('Cons')
        ) {
          // Parse floats safely
          const parse = (val: string) => {
            const num = parseFloat(val)
            return isNaN(num) ? 0 : num
          }

          const hrsSum = 
            parse(updatedRow.above6Hrs) + 
            parse(updatedRow.steamingHrs) + 
            parse(updatedRow.platformDpHrs) + 
            parse(updatedRow.atAnchorageHrs) + 
            parse(updatedRow.atBerthHrs)

          const consSum = 
            parse(updatedRow.above6Cons) + 
            parse(updatedRow.steamingCons) + 
            parse(updatedRow.platformDpCons) + 
            parse(updatedRow.atAnchorageCons) + 
            parse(updatedRow.atBerthCons)
            
          updatedRow.totalHr1 = hrsSum.toFixed(1)
          updatedRow.totalHr2 = hrsSum.toFixed(1) // Keep in sync for model compatibility
          updatedRow.totalFoCons = consSum.toFixed(3)
        }
        
        copy[index] = updatedRow
        return copy
      })
    }

    /* ── Navigation & Validation ── */
    const validateFoConsumption = () => {
      // Check if any row has an empty field (excluding remarks which might be optional)
      for (const row of foConsumptionRows) {
        if (!row.date || !row.above6Hrs || !row.above6Cons || !row.steamingHrs || !row.steamingCons || 
            !row.platformDpHrs || !row.platformDpCons || !row.atAnchorageHrs || !row.atAnchorageCons || 
            !row.atBerthHrs || !row.atBerthCons || !row.totalFoCons || !row.totalHr1) {
          return false
        }
        
        // Strict check: total hours must be exactly 24
        const totalHrs = parseFloat(row.totalHr1)
        if (totalHrs !== 24) {
          return false
        }
      }
      return true
    }

    const validateMachineryFoConsumption = () => {
      const enteredRows = machineryFoConsumptionRows.filter(
        (row) => row.machinery !== '' || row.foConsumption.trim() !== ''
      )
      if (enteredRows.length === 0) return false

      return enteredRows.every(
        (row) =>
          isValidMachineryOption(row.machinery) &&
          row.foConsumption.trim() !== '' &&
          !Number.isNaN(parseFloat(row.foConsumption))
      )
    }

    const validateFoConsumptionGate = () => {
      if (validateFoConsumption()) return true
      if (validateMachineryFoConsumption()) return true
      return false
    }

    const showFoConsumptionValidationToast = (includeOtherTabsMessage: boolean) => {
      if (!hasAnyExistingFoEntry() && !hasAnyMachineryFoEntry()) {
        toast.error('Please fill at least one FO Consumption entry before proceeding.', {
          position: 'top-center',
        })
        return
      }

      if (hasAnyMachineryFoEntry() && !validateMachineryFoConsumption()) {
        toast.error('Please complete at least one Machinery FO Consumption row with Machinery and numeric FO Consumption.', {
          position: 'top-center',
        })
        return
      }

      const invalidRow = foConsumptionRows.some((row) => parseFloat(row.totalHr1) !== 24)

      if (invalidRow) {
        toast.error('Total hours must be exactly 24 hours per day in FO Consumption.', {
          position: 'top-center',
        })
        return
      }

      toast.error(
        includeOtherTabsMessage
          ? 'Please fill out all F.O. Consumption fields before proceeding to other tabs.'
          : 'Please fill out all F.O. Consumption fields before proceeding.',
        {
          position: 'top-center',
        }
      )
    }

    const getSectionPayload = (tab: TabKey) => {
      switch (tab) {
        case 'general':
          return {
            reportDate,
            vesselName,
            voyageNo,
            portOrSea,
            fromLocation,
            toLocation,
            eta,
            draftFwd,
            draftAft,
            weather,
            wind,
            seaState,
            dlDate,
            dlTime,
            dlLocation,
            dlPosition: buildPositionValue(positionFields) || dlPosition,
          }
        case 'foConsumption':
          return {
            foConsumptionRows,
            machineryFoConsumptionRows: getValidMachineryFoRows(),
          }
        case 'activity':
          return { activities }
        case 'activitySummary':
          return {
            activitySummary: {
              speedsUpto6,
              speedsAbove6,
              inDpMode,
              atAnchorageBerth,
              downTime: downTimeSummary,
              othersSpecify,
              othersLabel,
              total,
              fuelConsumedIn24Hrs,
            },
          }
        case 'robStatus':
          return { robStatus: robRows }
        case 'weather':
          return { weatherRows, tides }
        case 'downtime':
          return { downtimeRows }
        case 'pob':
          return { pobRows }
        case 'statisticsSafety':
          return { safetyLeft, safetyRight, equipmentRows }
        case 'passengerDetails':
          return { passengerRows }
        case 'engineRunningHours':
          return { engineRunningHours: engineRunningHoursRows }
        case 'engineParam':
          return { engineParamRows }
        case 'exhTemp':
          return { exhTempRows }
        default:
          return {}
      }
    }

    const handleTabClick = (tab: TabKey) => {
      if (tab !== 'general' && tab !== 'foConsumption') {
        const isValid = validateFoConsumptionGate()
        if (!isValid) {
          showFoConsumptionValidationToast(true)
          return
        }
      }

      if (reportId) {
        const currentSection = TAB_TO_SECTION[activeTab]
        autoSave.triggerAutoSave(currentSection, getSectionPayload(activeTab), true)
        const targetSection = TAB_TO_SECTION[tab]
        realtime.acquireLease(targetSection)
      }

      setActiveTab(tab)
    }

    const nextTab = () => {
      if (activeTab === 'foConsumption') {
        const isValid = validateFoConsumptionGate()
        if (!isValid) {
          showFoConsumptionValidationToast(false)
          return
        }
      }
      const i = TABS.indexOf(activeTab)
      if (i < TABS.length - 1) setActiveTab(TABS[i + 1])
    }
    const prevTab = () => {
      const i = TABS.indexOf(activeTab)
      if (i > 0) setActiveTab(TABS[i - 1])
    }

    /* ── Save ── */
    const handleFinalSave = async (status: 'DRAFT' | 'SUBMITTED' = 'SUBMITTED') => {
      if (saveInFlightRef.current) return

      const currentVesselId = editData?.vesselId || vesselId || resolveDefaultVesselId()
      if (!currentVesselId || currentVesselId === 0) {
        toast.error('Please select a vessel before saving the DPR.', { position: 'top-center' })
        return
      }

      if (status === 'SUBMITTED') {
        const nextPositionErrors = validateDprShipPositionFinal(positionFields)
        if (Object.keys(nextPositionErrors).length > 0) {
          setPositionErrors(nextPositionErrors)
          setActiveTab('general')
          const firstMsg =
            nextPositionErrors.startLatDeg ||
            nextPositionErrors.startLatMin ||
            nextPositionErrors.startLatSec ||
            nextPositionErrors.startLonDeg ||
            nextPositionErrors.startLonMin ||
            nextPositionErrors.startLonSec ||
            'Please correct the Position Latitude/Longitude fields before saving.'
          toast.error(firstMsg, {
            position: 'top-center',
          })
          return
        }

        const hasNegativeRobClosing = robRows.some((row) => {
          if (!isFuelCommodity(row.commodity)) return false

          const opening = toNumberOrZero(row.openingStock)
          const received = toNumberOrZero(row.received)
          const deliveredToOtherVessel = toNumberOrZero(row.deliveredToOtherVessel)
          const deliveredToPlatforms = toNumberOrZero(row.deliveredToPlatforms)
          const consumedByVessel = toNumberOrZero(row.consumedByVessel)
          return opening + received - deliveredToOtherVessel - deliveredToPlatforms - consumedByVessel < 0
        })

        if (hasNegativeRobClosing) {
          setActiveTab('robStatus')
          toast.error('Closing stock cannot be negative. Please adjust ROB values.', {
            position: 'top-center',
            toastId: ROB_NEGATIVE_TOAST_ID,
          })
          return
        }

        if (requiresInitialRobOpening) {
          const missingOpeningStock = robRows.some((row) => row.openingStock.trim() === '')
          if (missingOpeningStock) {
            setActiveTab('robStatus')
            toast.error('Please fill the ROB opening stock only for the vessel\'s first DPR.', {
              position: 'top-center',
              toastId: ROB_FIRST_DPR_TOAST_ID,
            })
            return
          }
        }
      }

      saveInFlightRef.current = true
      setIsSaving(true)
      try {
        const resolvedDlPosition = buildPositionValue(positionFields) || dlPosition
        const normalizedFoRows = [...foConsumptionRows]
        if (hasValidMachineryFoRows) {
          const firstRow = normalizedFoRows[0] ?? blankFoConsumptionRow()
          normalizedFoRows[0] = {
            ...firstRow,
            totalFoCons: machineryFoConsumptionTotal.toFixed(3),
          }
        }

        const record: DailyPositionReportRecord = {
          id: editData?.id ?? Date.now(),
          vesselId: editData?.vesselId || vesselId || resolveDefaultVesselId(),
          reportDate,
          vesselName,
          voyageNo,
          portOrSea,
          fromLocation,
          toLocation,
          eta,
          draftFwd,
          draftAft,
          weather,
          wind,
          seaState,
          foConsumptionRows: normalizedFoRows,
          machineryFoConsumptionRows: getValidMachineryFoRows(),
          dlDate,
          dlTime,
          dlLocation,
          dlPosition: resolvedDlPosition,
          activities,
          activitySummary: {
            speedsUpto6,
            speedsAbove6,
            inDpMode,
            atAnchorageBerth,
            downTime: downTimeSummary,
            othersSpecify,
            othersLabel,
            total,
            fuelConsumedIn24Hrs,
          },
          robStatus: robRows,
          lubeOilStatusRows,
          weatherRows,
          tides,
          downtimeRows,
          pobRows,
          safetyLeft,
          safetyRight,
          equipmentRows,
          passengerRows,
          engineTemp: {
            params: engineParamRows,
            exhTemps: exhTempRows,
          },
          engineRunningHours: engineRunningHoursRows,
          status: status,
          createdAt: editData?.createdAt ?? new Date().toISOString(),
        }
        await Promise.resolve(onSave(record))
      } finally {
        saveInFlightRef.current = false
        setIsSaving(false)
      }
    }

    const isLastTab = activeTab === TABS[TABS.length - 1]

    const handleInputFocusCapture = (event: React.FocusEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement
      if (!(target instanceof HTMLInputElement)) return
      if (target.readOnly || target.disabled) return
      if (target.type !== 'text' && target.type !== 'number') return
      if (!isZeroLikeInputValue(target.value)) return

      target.value = ''
      target.dispatchEvent(new Event('input', {bubbles: true}))
    }

    if (!isOpen) return null

    /* ═══════════════════════════════ RENDER ═══════════════════════════════ */
    return (
      <div className='modal-overlay'>
        <ToastContainer position='top-center' autoClose={3000} newestOnTop closeOnClick draggable pauseOnHover />
        <div
          className='modal-content'
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '80rem' }}
        >
          {/* ── Header ── */}
          <div className='custom-modal-header d-flex justify-content-between align-items-center'>
            <h5 className='m-0'>{editData ? 'Edit Daily Progress Report' : 'Add Daily Progress Report'}</h5>
            <button className='close-btn' onClick={onClose}>
              <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
            </button>
          </div>

          {/* ── Real-Time Auto-Save & Collaborators Status Bar (Active during edit) ── */}
          {editData && (
            <DprRealtimeStatusBar
              saveStatus={autoSave.saveStatus}
              lastSavedTime={autoSave.lastSavedTime}
              lastError={autoSave.lastError}
              isConnected={realtime.isConnected}
              leases={realtime.leases}
              onOpenHistory={() => setShowHistoryDrawer(true)}
            />
          )}

          {/* ── Tabs ── */}
          <ul className='nav nav-tabs mb-4' style={{ flexWrap: 'wrap' }}>
            {TABS.map((tab) => (
              <li className='nav-item' key={tab}>
                <button
                  className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    handleTabClick(tab)
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {TAB_LABELS[tab]}
                </button>
              </li>
            ))}
          </ul>

          <style>{`
            .dpr-modal-table-scope table thead th {
              background-color: #eaf3ff !important;
            }
            .dpr-modal-table-scope table tbody td,
            .dpr-modal-table-scope table tbody th {
              background-color: #ffffff !important;
            }
            .dpr-modal-table-scope table tbody tr:nth-child(odd) td,
            .dpr-modal-table-scope table tbody tr:nth-child(odd) th,
            .dpr-modal-table-scope table tbody tr:nth-child(even) td,
            .dpr-modal-table-scope table tbody tr:nth-child(even) th {
              background-color: #ffffff !important;
            }
          `}</style>

          {/* ── Tab body ── */}
          <div
            className='custom-modal-body dpr-modal-table-scope'
            style={{ maxHeight: '65vh', overflowY: 'auto' }}
            onFocusCapture={handleInputFocusCapture}
          >

            {/* ─────────── TAB 0: FO Consumption ─────────── */}
            {activeTab === 'foConsumption' && (
              <div>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='m-0'>Machinery FO Consumption</h6>
                  <button className='btn btn-sm btn-primary' onClick={addMachineryFoConsumptionRow}>+ Add Row</button>
                </div>
                <div className='table-responsive mb-4'>
                  <table className='table table-bordered table-sm align-middle text-center mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th style={{ width: '90px' }}>Sr. No</th>
                        <th style={{ width: '180px' }}>Machinery</th>
                        <th style={{ width: '180px' }}>FO Consumption</th>
                        <th style={{ width: '80px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {machineryFoConsumptionRows.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            {(() => {
                              const rowOptions = getMachineryOptionsForRow(machineryFoConsumptionRows, idx)
                              return (
                            <SearchableSelect
                              className='form-select form-select-sm'
                              value={row.machinery}
                              onChange={(e) => handleMachineryFoConsumptionChange(idx, 'machinery', e.target.value)}
                            >
                              <option value=''>Select Machinery</option>
                              {rowOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </SearchableSelect>
                              )
                            })()}
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end'
                              placeholder='0.000'
                              value={row.foConsumption}
                              onChange={(e) => handleMachineryFoConsumptionChange(idx, 'foConsumption', e.target.value)}
                            />
                          </td>
                          <td>
                            <button
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => removeMachineryFoConsumptionRow(idx)}
                              disabled={machineryFoConsumptionRows.length === 1}
                              title='Remove'
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle text-center mb-0' style={{ fontSize: '0.85rem' }}>
                    <thead className='table-light'>
                      <tr>
                        <th rowSpan={2} className='align-middle'>DATE</th>
                        <th colSpan={2}>Above 6 Kts</th>
                        <th colSpan={2}>STEAMING (up to 6 Kts)</th>
                        <th colSpan={2}>PLATFORM/DP</th>
                        <th colSpan={2}>AT ANCHORAGE</th>
                        <th colSpan={2}>AT BERTH</th>
                        <th rowSpan={2} className='align-middle'>TOTAL F.O.<br/>CONS (IN KL)</th>
                        <th rowSpan={2} className='align-middle'>TOTAL HR</th>
                        <th rowSpan={2} className='align-middle'>REMARKS</th>
                      </tr>
                      <tr>
                        <th>HRS</th>
                        <th>F.O CONS (KL)</th>
                        <th>HRS</th>
                        <th>F.O CONS (KL)</th>
                        <th>HRS</th>
                        <th>F.O CONS (KL)</th>
                        <th>HRS</th>
                        <th>F.O CONS (KL)</th>
                        <th>HRS</th>
                        <th>F.O CONS (KL)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {foConsumptionRows.map((row, idx) => (
                        <tr key={idx}>
                          <td style={{ minWidth: '110px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='DD/MM/YYYY' value={row.date} onChange={(e) => handleFoConsumptionChange(idx, 'date', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '60px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.0' value={row.above6Hrs} onChange={(e) => handleFoConsumptionChange(idx, 'above6Hrs', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '70px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.000' value={row.above6Cons} onChange={(e) => handleFoConsumptionChange(idx, 'above6Cons', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '60px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.0' value={row.steamingHrs} onChange={(e) => handleFoConsumptionChange(idx, 'steamingHrs', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '70px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.000' value={row.steamingCons} onChange={(e) => handleFoConsumptionChange(idx, 'steamingCons', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '60px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.0' value={row.platformDpHrs} onChange={(e) => handleFoConsumptionChange(idx, 'platformDpHrs', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '70px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.000' value={row.platformDpCons} onChange={(e) => handleFoConsumptionChange(idx, 'platformDpCons', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '60px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.0' value={row.atAnchorageHrs} onChange={(e) => handleFoConsumptionChange(idx, 'atAnchorageHrs', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '70px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.000' value={row.atAnchorageCons} onChange={(e) => handleFoConsumptionChange(idx, 'atAnchorageCons', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '60px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.0' value={row.atBerthHrs} onChange={(e) => handleFoConsumptionChange(idx, 'atBerthHrs', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '70px' }}>
                            <input type='text' className='form-control form-control-sm text-center px-1' placeholder='0.000' value={row.atBerthCons} onChange={(e) => handleFoConsumptionChange(idx, 'atBerthCons', e.target.value)} />
                          </td>
                          <td style={{ minWidth: '80px' }}>
                            <input
                              type='text'
                              className='form-control form-control-sm text-center px-1'
                              placeholder='0.000'
                              value={idx === 0 && hasValidMachineryFoRows ? machineryFoConsumptionTotal.toFixed(3) : row.totalFoCons}
                              onChange={(e) => handleFoConsumptionChange(idx, 'totalFoCons', e.target.value)}
                              readOnly={idx === 0 && hasValidMachineryFoRows}
                            />
                          </td>
                          <td style={{ minWidth: '60px' }}>
                            <input 
                              type='text' 
                              className={`form-control form-control-sm text-center px-1 ${foConsumptionTouched && parseFloat(row.totalHr1) !== 24 ? 'text-danger fw-bold border-danger' : ''}`}
                              placeholder='0.0' 
                              value={row.totalHr1} 
                              readOnly 
                            />
                          </td>
                          <td style={{ minWidth: '100px' }}>
                            <input type='text' className='form-control form-control-sm px-1' placeholder='Remarks' value={row.remarks} onChange={(e) => handleFoConsumptionChange(idx, 'remarks', e.target.value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {foConsumptionTouched && foConsumptionRows.some((r) => parseFloat(r.totalHr1) !== 24) && (
                  <div className='alert alert-danger d-flex align-items-center p-2 mb-3 mt-3' style={{ fontSize: '0.8rem' }}>
                    <KTSVG path='/media/map/alert.svg' className='svg-icon-2x me-2' />
                    <span>Total hours in one or more rows must be exactly 24 hours. Please correct this before proceeding.</span>
                  </div>
                )}
              </div>
            )}

            {/* ─────────── TAB 1: Date and Time ─────────── */}
            {activeTab === 'general' && (
              <div>
                {/* ── 1. DATE & LOCATION ── */}
                <h6 className='fw-bold mb-3'>1. Ships Location</h6>
                <div className='table-responsive mb-4'>
                  <table className='table table-bordered table-sm align-middle mb-0'>
                    <tbody>
                      <tr>
                        <td className='fw-bold bg-light' style={{width: '120px'}}>Report Date :</td>
                        <td>
                          <FormattedDateInput className='form-control form-control-sm' value={reportDate} onChange={setReportDate} />
                        </td>
                        <td className='fw-bold bg-light' style={{width: '120px'}}>Vessel Name:</td>
                        <td className='align-middle px-3'>
                          {!editData && selectedVesselIdNum === 0 && vesselOptions && vesselOptions.length > 0 ? (
                            <SearchableSelect
                              className='form-select form-select-sm'
                              value={vesselId || ''}
                              onChange={(e) => {
                                const newId = Number(e.target.value)
                                setVesselId(newId)
                                const selectedOpt = vesselOptions.find(v => v.id === newId)
                                setVesselName(selectedOpt?.name || '')
                                if (newId > 0) {
                                  setVoyageNo('Loading...')
                                  loadDefaultVoyageNo(newId, selectedOpt?.name)
                                    .then((resolvedVoyageNo) => {
                                      setVoyageNo(resolvedVoyageNo ?? '')
                                    })
                                    .catch((err: any) => {
                                      console.error("Failed to fetch voyages", err)
                                      setVoyageNo('')
                                    })
                                } else {
                                  setVoyageNo('')
                                }
                              }}
                            >
                              <option value=''>-- Select Vessel --</option>
                              {vesselOptions.map(vo => (
                                <option key={vo.id} value={vo.id}>{vo.name}</option>
                              ))}
                            </SearchableSelect>
                          ) : (
                            <span className='fw-semibold text-gray-800'>{vesselName || '—'}</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold bg-light'>Voyage No :</td>
                        <td colSpan={3} className='align-middle px-3'>
                          <span className='fw-semibold text-gray-800'>{voyageNo || '—'}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h6 className='fw-bold mb-3'>2. POSITION</h6>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle mb-0'>
                    <tbody>
                      <tr>
                        <td className='fw-bold bg-light' style={{width: '120px'}}>Location:</td>
                        <td colSpan={3}>
                          <input type='text' className='form-control form-control-sm' placeholder='e.g. LAXMI-GAURI OIL FIELD' value={dlLocation} onChange={(e) => setDlLocation(e.target.value)} />
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold bg-light'>Position:</td>
                        <td colSpan={3}>
                          <div className='row g-3'>
                            <div className='col-md-6'>
                              <label className='form-label small text-muted mb-1'>Position Latitude <span className='text-danger'>*</span></label>
                              <div className='d-flex gap-2'>
                                <div className='input-group input-group-sm'>
                                  <input type='text' maxLength={8} className='form-control' value={positionFields.startLatDeg} onChange={(e) => handlePositionFieldChange('startLatDeg', e.target.value)} />
                                  <span className='input-group-text'>Deg</span>
                                </div>
                                <div className='input-group input-group-sm'>
                                  <input type='text' maxLength={8} className='form-control' value={positionFields.startLatMin} onChange={(e) => handlePositionFieldChange('startLatMin', e.target.value)} />
                                  <span className='input-group-text'>min</span>
                                </div>
                                <div className='input-group input-group-sm'>
                                  <input type='text' maxLength={12} className='form-control' value={positionFields.startLatSec} onChange={(e) => handlePositionFieldChange('startLatSec', e.target.value)} />
                                  <span className='input-group-text'>sec</span>
                                </div>
                                <select
                                   className='form-select form-select-sm fw-bold text-center'
                                   style={{ width: '65px', minWidth: '65px', flexShrink: 0 }}
                                   value={positionFields.startLatDir}
                                   onChange={(e) => handlePositionFieldChange('startLatDir', e.target.value)}
                                 >
                                   <option value='N'>N</option>
                                   <option value='S'>S</option>
                                 </select>
                              </div>
                              {positionErrors.startLatDeg && <div className='text-danger small mt-1'>{positionErrors.startLatDeg}</div>}
                              {positionErrors.startLatMin && <div className='text-danger small mt-1'>{positionErrors.startLatMin}</div>}
                              {positionErrors.startLatSec && <div className='text-danger small mt-1'>{positionErrors.startLatSec}</div>}
                            </div>
                            <div className='col-md-6'>
                              <label className='form-label small text-muted mb-1'>Position Longitude <span className='text-danger'>*</span></label>
                              <div className='d-flex gap-2'>
                                <div className='input-group input-group-sm'>
                                  <input type='text' maxLength={10} className='form-control' value={positionFields.startLonDeg} onChange={(e) => handlePositionFieldChange('startLonDeg', e.target.value)} />
                                  <span className='input-group-text'>Deg</span>
                                </div>
                                <div className='input-group input-group-sm'>
                                  <input type='text' maxLength={8} className='form-control' value={positionFields.startLonMin} onChange={(e) => handlePositionFieldChange('startLonMin', e.target.value)} />
                                  <span className='input-group-text'>min</span>
                                </div>
                                <div className='input-group input-group-sm'>
                                  <input type='text' maxLength={12} className='form-control' value={positionFields.startLonSec} onChange={(e) => handlePositionFieldChange('startLonSec', e.target.value)} />
                                  <span className='input-group-text'>sec</span>
                                </div>
                                 <select
                                   className='form-select form-select-sm fw-bold text-center'
                                   style={{ width: '65px', minWidth: '65px', flexShrink: 0 }}
                                   value={positionFields.startLonDir}
                                   onChange={(e) => handlePositionFieldChange('startLonDir', e.target.value)}
                                 >
                                   <option value='E'>E</option>
                                   <option value='W'>W</option>
                                 </select>
                              </div>
                              {positionErrors.startLonDeg && <div className='text-danger small mt-1'>{positionErrors.startLonDeg}</div>}
                              {positionErrors.startLonMin && <div className='text-danger small mt-1'>{positionErrors.startLonMin}</div>}
                              {positionErrors.startLonSec && <div className='text-danger small mt-1'>{positionErrors.startLonSec}</div>}
                            </div>
                            {!buildPositionValue(positionFields) && dlPosition && (
                              <div className='col-12'>
                                <small className='text-muted'>Existing saved position: {dlPosition}</small>
                              </div>
                            )}
                            {/* Mini Map Preview */}
                            <div className='col-12 mt-4'>
                              {(() => {
                                const currentPosString = buildPositionValue(positionFields) || dlPosition
                                const decimals = parseFleetDprDlPositionToDecimals(currentPosString)
                                return (
                                  <DprMiniMap lat={decimals?.lat ?? null} lon={decimals?.lon ?? null} height='250px' />
                                )
                              })()}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 2: Activity ─────────── */}
            {activeTab === 'activity' && (
              <div>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <h6 className='m-0'>Activity Log</h6>
                  <button className='btn btn-sm btn-primary' onClick={addActivityRow}>+ Add Row</button>
                </div>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th style={{ width: '130px' }}>Local Time (From)</th>
                        <th style={{ width: '130px' }}>Local Time (To)</th>
                        <th>Activity Description</th>
                        <th style={{ width: '200px' }}>Position</th>
                        <th style={{ width: '60px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((act, idx) => (
                        <tr key={act.id}>
                          <td><input type='text' className='form-control form-control-sm' placeholder='0000' value={act.localTimeFrom} onChange={(e) => handleActivityChange(idx, 'localTimeFrom', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm' placeholder='0948' value={act.localTimeTo} onChange={(e) => handleActivityChange(idx, 'localTimeTo', e.target.value)} /></td>
                          <td><textarea className='form-control form-control-sm' rows={2} placeholder='Describe activity…' value={act.description} onChange={(e) => handleActivityChange(idx, 'description', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm' placeholder='Lat/Lon' value={act.position} onChange={(e) => handleActivityChange(idx, 'position', e.target.value)} /></td>
                          <td className='text-center'>
                            {activities.length > 1 && (
                              <button className='btn btn-sm btn-outline-danger' onClick={() => removeActivityRow(idx)} title='Remove'>✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 3: Activity Summary ─────────── */}
            {activeTab === 'activitySummary' && (
              <div>
                <h6 className='mb-3'>Activity Summary</h6>
                <div className='table-responsive mb-4'>
                  <table className='table table-bordered table-sm align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th></th>
                        <th className='text-center'>Speeds ≤6 KTS</th>
                        <th className='text-center'>Speeds &gt;6 KTS</th>
                        <th className='text-center'>In DP Mode</th>
                        <th className='text-center'>At Anchorage / Berth</th>
                        <th className='text-center'>Down time</th>
                        <th className='text-center'>Others (specify)</th>
                        <th className='text-center'>Total (hh:mm)</th>
                        <th className='text-center'>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className='fw-bold'>Time (hh:mm)</td>
                        <td><input type='text' className='form-control form-control-sm text-center bg-light' placeholder='0.0' value={speedsUpto6} readOnly /></td>
                        <td><input type='text' className='form-control form-control-sm text-center bg-light' placeholder='0.0' value={speedsAbove6} readOnly /></td>
                        <td><input type='text' className='form-control form-control-sm text-center bg-light' placeholder='0.0' value={inDpMode} readOnly /></td>
                        <td><input type='text' className='form-control form-control-sm text-center bg-light' placeholder='0.0' value={atAnchorageBerth} readOnly /></td>
                        <td><input type='text' className='form-control form-control-sm text-center' placeholder='0.0' value={downTimeSummary} onChange={(e) => setDownTimeSummary(e.target.value)} /></td>
                        <td><input type='text' className='form-control form-control-sm text-center' placeholder='0.0' value={othersSpecify} onChange={(e) => setOthersSpecify(e.target.value)} /></td>
                        <td><input type='text' className='form-control form-control-sm text-center' placeholder='24.0' value={total} onChange={(e) => setTotal(e.target.value)} /></td>
                        <td><input type='text' className='form-control form-control-sm' placeholder='' value={othersLabel} onChange={(e) => setOthersLabel(e.target.value)} /></td>
                      </tr>
                      <tr>
                        <td className='fw-bold'>Fuel consumed in 24 hrs</td>
                        <td colSpan={4}><input type='text' className='form-control form-control-sm bg-light' placeholder='e.g. 0.838' value={fuelConsumedIn24Hrs} readOnly /></td>
                        <td colSpan={4}><small className='text-muted'>Please specify clearly the details like fuel consumed during downtime, details if Total (hh:mm) is less than 24 hours &amp; other relevant details under Remarks column.</small></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 4: ROB Status ─────────── */}
            {activeTab === 'robStatus' && (
              <div>
                <h6 className='mb-3'>4. ROB Status</h6>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th>Commodity</th>
                        <th className='text-center'>Opening Stock</th>
                        <th className='text-center'>Received</th>
                        <th className='text-center'>Delivered to other Vessel</th>
                        <th className='text-center'>Delivered to Platforms</th>
                        <th className='text-center'>Consumed by Vessel</th>
                        <th className='text-center'>Closing Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {robRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className='fw-bold text-nowrap'>{row.commodity}</td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.openingStock ?? ''} onChange={(e) => handleRobChange(idx, 'openingStock', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.received ?? ''} onChange={(e) => handleRobChange(idx, 'received', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.deliveredToOtherVessel ?? ''} onChange={(e) => handleRobChange(idx, 'deliveredToOtherVessel', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.deliveredToPlatforms ?? ''} onChange={(e) => handleRobChange(idx, 'deliveredToPlatforms', e.target.value)} /></td>
                          <td><input type='text' className={`form-control form-control-sm text-center ${isFuelCommodity(row.commodity) ? 'bg-light' : ''}`} value={row.consumedByVessel ?? ''} onChange={(e) => handleRobChange(idx, 'consumedByVessel', e.target.value)} readOnly={isFuelCommodity(row.commodity)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center bg-light' value={row.closingStock ?? ''} readOnly /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className='d-flex justify-content-between align-items-center mb-3 mt-4'>
                  <h6 className='m-0'>Lube Oil Status</h6>
                  <button className='btn btn-sm btn-primary' onClick={addLubeOilStatusRow}>+ Add Row</button>
                </div>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle text-center'>
                    <thead className='table-light'>
                      <tr>
                        <th style={{ width: '90px' }}>Sr. No</th>
                        <th style={{ width: '180px' }}>Grades</th>
                        <th style={{ width: '150px' }}>Opening Stock</th>
                        <th style={{ width: '150px' }}>Received</th>
                        <th style={{ width: '170px' }}>Consumed by Vessel</th>
                        <th style={{ width: '150px' }}>Closing Stock</th>
                        <th style={{ width: '80px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lubeOilStatusRows.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            {(() => {
                              if (customLubeOilRows[idx]) {
                                return (
                                  <div className='input-group input-group-sm'>
                                    <input
                                      type='text'
                                      className='form-control form-control-sm'
                                      placeholder='Enter Grade'
                                      value={row.machinery}
                                      onChange={(e) => handleLubeOilStatusChange(idx, 'machinery', e.target.value)}
                                      autoFocus
                                    />
                                    <button
                                      type='button'
                                      className='btn btn-outline-secondary px-2'
                                      title='Select from List'
                                      onClick={() => {
                                        setCustomLubeOilRows((prev) => {
                                          const next = { ...prev }
                                          delete next[idx]
                                          return next
                                        })
                                        handleLubeOilStatusChange(idx, 'machinery', '')
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                )
                              }
                              const rowOptions = getLubeGradeOptionsForRow(lubeOilStatusRows, idx)
                              return (
                                <SearchableSelect
                                  className='form-select form-select-sm'
                                  value={row.machinery}
                                  onChange={(e) => {
                                    if (e.target.value === 'Others') {
                                      setCustomLubeOilRows((prev) => ({ ...prev, [idx]: true }))
                                      handleLubeOilStatusChange(idx, 'machinery', '')
                                    } else {
                                      handleLubeOilStatusChange(idx, 'machinery', e.target.value)
                                    }
                                  }}
                                >
                                  <option value=''>Select Grade</option>
                                  {rowOptions.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                  <option value='Others'>Others</option>
                                </SearchableSelect>
                              )
                            })()}
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end'
                              placeholder='0.000'
                              value={displayLubeValue(row.openingStock)}
                              onChange={(e) => handleLubeOilStatusChange(idx, 'openingStock', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end'
                              placeholder='0.000'
                              value={displayLubeValue(row.received)}
                              onChange={(e) => handleLubeOilStatusChange(idx, 'received', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end'
                              placeholder='0.000'
                              value={displayLubeValue(row.consumedByVessel)}
                              onChange={(e) => handleLubeOilStatusChange(idx, 'consumedByVessel', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end bg-light'
                              placeholder='0.000'
                              value={displayLubeValue(row.closingStock)}
                              readOnly
                            />
                          </td>
                          <td>
                            <button
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => removeLubeOilStatusRow(idx)}
                              disabled={lubeOilStatusRows.length === 1}
                              title='Remove'
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 5: Weather ─────────── */}
            {activeTab === 'weather' && (
              <div>
                <h6 className='mb-3'>5. Weather</h6>
                <div className='table-responsive mb-4'>
                  <table className='table table-bordered table-sm align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th>Local Time</th>
                        <th className='text-center'>Wind Force</th>
                        <th className='text-center'>Wind Spd</th>
                        <th className='text-center'>Direction</th>
                        <th className='text-center'>Sea/Swell Ht</th>
                        <th className='text-center'>Direction</th>
                        <th className='text-center'>Current Rate</th>
                        <th className='text-center'>Direction</th>
                        <th className='text-center'>Baro</th>
                        <th className='text-center'>Sky</th>
                        <th className='text-center'>Vis</th>
                        <th className='text-center'>TEMP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weatherRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className='fw-bold text-center'>{row.localTime}</td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.windForce} onChange={(e) => handleWeatherChange(idx, 'windForce', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.windSpd} onChange={(e) => handleWeatherChange(idx, 'windSpd', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.direction} onChange={(e) => handleWeatherChange(idx, 'direction', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.seaSwellHt} onChange={(e) => handleWeatherChange(idx, 'seaSwellHt', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.swellDirection} onChange={(e) => handleWeatherChange(idx, 'swellDirection', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.currentRate} onChange={(e) => handleWeatherChange(idx, 'currentRate', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.currentDirection} onChange={(e) => handleWeatherChange(idx, 'currentDirection', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.baro} onChange={(e) => handleWeatherChange(idx, 'baro', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.sky} onChange={(e) => handleWeatherChange(idx, 'sky', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.vis} onChange={(e) => handleWeatherChange(idx, 'vis', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.temp} onChange={(e) => handleWeatherChange(idx, 'temp', e.target.value)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Tides sub-table */}
                <h6 className='mb-2'>Tides</h6>
                <div className='row g-3 mb-3'>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>AM-LW (Time)</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 1126' value={tides.amLw} onChange={(e) => handleTideChange('amLw', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>AM-HW (Time)</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 0409' value={tides.amHw} onChange={(e) => handleTideChange('amHw', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>PM-LW (Time)</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 2318' value={tides.pmLw} onChange={(e) => handleTideChange('pmLw', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>PM-HW (Time)</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 1613' value={tides.pmHw} onChange={(e) => handleTideChange('pmHw', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>AM-LW Height</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 1.51' value={tides.amLwHeight} onChange={(e) => handleTideChange('amLwHeight', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>AM-HW Height</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 7.81' value={tides.amHwHeight} onChange={(e) => handleTideChange('amHwHeight', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>PM-LW Height</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 1.23' value={tides.pmLwHeight} onChange={(e) => handleTideChange('pmLwHeight', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>PM-HW Height</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 6.22' value={tides.pmHwHeight} onChange={(e) => handleTideChange('pmHwHeight', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>Sunrise</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 6:41:00 AM' value={tides.sunrise} onChange={(e) => handleTideChange('sunrise', e.target.value)} />
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-bold text-dark fs-7'>Sunset</label>
                    <input type='text' className='form-control form-control-sm' placeholder='e.g. 6:06:00 PM' value={tides.sunset} onChange={(e) => handleTideChange('sunset', e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {/* ─────────── TAB 6: Downtime ─────────── */}
            {activeTab === 'downtime' && (
              <div>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <h6 className='m-0'>6. Downtime</h6>
                  <button className='btn btn-sm btn-primary' onClick={addDowntimeRow}>+ Add Row</button>
                </div>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th className='text-center'>From</th>
                        <th className='text-center'>To</th>
                        <th className='text-center'>Today</th>
                        <th className='text-center'>Previous</th>
                        <th className='text-center'>Total (dd:hh:mm)</th>
                        <th>Reasons / Remarks</th>
                        <th style={{ width: '60px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downtimeRows.map((row, idx) => (
                        <tr key={row.id}>
                          <td><input type='text' className='form-control form-control-sm text-center' placeholder='NIL' value={row.from} onChange={(e) => handleDowntimeChange(idx, 'from', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' placeholder='NIL' value={row.to} onChange={(e) => handleDowntimeChange(idx, 'to', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' placeholder='NIL' value={row.today} onChange={(e) => handleDowntimeChange(idx, 'today', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' placeholder='NIL' value={row.previous} onChange={(e) => handleDowntimeChange(idx, 'previous', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' placeholder='NIL' value={row.total} onChange={(e) => handleDowntimeChange(idx, 'total', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm' placeholder='Reasons / Remarks' value={row.reasonsRemarks} onChange={(e) => handleDowntimeChange(idx, 'reasonsRemarks', e.target.value)} /></td>
                          <td className='text-center'>
                            {downtimeRows.length > 1 && (
                              <button className='btn btn-sm btn-outline-danger' onClick={() => removeDowntimeRow(idx)} title='Remove'>✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 7: P.O.B ─────────── */}
            {activeTab === 'pob' && (
              <div>
                <h6 className='mb-3'>7. P.O.B (Persons On Board)</h6>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th className='text-center'>S.No</th>
                        <th>Company</th>
                        <th className='text-center'>Previous</th>
                        <th className='text-center'>Pax-In</th>
                        <th className='text-center'>Pax-Out</th>
                        <th className='text-center'>Total</th>
                        <th className='text-center' colSpan={3}>Meals by CAIRN / CAIRN Contractor</th>
                        <th>Remarks</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th className='text-center'>Breakfast</th>
                        <th className='text-center'>Lunch</th>
                        <th className='text-center'>Dinner</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pobRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className='text-center'>{row.sNo}</td>
                          <td className='fw-bold'>{row.company}</td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.previous} onChange={(e) => handlePobChange(idx, 'previous', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.paxIn} onChange={(e) => handlePobChange(idx, 'paxIn', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.paxOut} onChange={(e) => handlePobChange(idx, 'paxOut', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.total} onChange={(e) => handlePobChange(idx, 'total', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.mealsBreakfast} onChange={(e) => handlePobChange(idx, 'mealsBreakfast', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.mealsLunch} onChange={(e) => handlePobChange(idx, 'mealsLunch', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.mealsDinner} onChange={(e) => handlePobChange(idx, 'mealsDinner', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm' value={row.remarks} onChange={(e) => handlePobChange(idx, 'remarks', e.target.value)} /></td>
                        </tr>
                      ))}
                      {/* Total row */}
                      <tr className='table-warning fw-bold'>
                        <td></td>
                        <td>Total</td>
                        <td className='text-center'>{pobRows.reduce((s, r) => s + (parseInt(r.previous) || 0), 0)}</td>
                        <td className='text-center'>{pobRows.reduce((s, r) => s + (parseInt(r.paxIn) || 0), 0)}</td>
                        <td className='text-center'>{pobRows.reduce((s, r) => s + (parseInt(r.paxOut) || 0), 0)}</td>
                        <td className='text-center'>{pobRows.reduce((s, r) => s + (parseInt(r.total) || 0), 0)}</td>
                        <td className='text-center'>{pobRows.reduce((s, r) => s + (parseInt(r.mealsBreakfast) || 0), 0)}</td>
                        <td className='text-center'>{pobRows.reduce((s, r) => s + (parseInt(r.mealsLunch) || 0), 0)}</td>
                        <td className='text-center'>{pobRows.reduce((s, r) => s + (parseInt(r.mealsDinner) || 0), 0)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 8: Statistics & Safety Checks ─────────── */}
            {activeTab === 'statisticsSafety' && (
              <div>
                <h6 className='mb-3'>8. Statistics &amp; Safety Checks</h6>
                <div className='row'>
                  {/* Left table */}
                  <div className='col-md-6'>
                    <div className='table-responsive mb-3'>
                      <table className='table table-bordered table-sm align-middle'>
                        <thead className='table-light'>
                          <tr>
                            <th>KPI's</th>
                            <th className='text-center'>Previous Day</th>
                            <th className='text-center'>Today</th>
                            <th className='text-center'>Cumulative</th>
                          </tr>
                        </thead>
                        <tbody>
                          {safetyLeft.map((row, idx) => (
                            <tr key={idx}>
                              <td className='text-nowrap small'>{row.particulars}</td>
                              <td><input type='text' className='form-control form-control-sm text-center' value={row.previousDay} onChange={(e) => handleSafetyLeftChange(idx, 'previousDay', e.target.value)} /></td>
                              <td><input type='text' className='form-control form-control-sm text-center' value={row.today} onChange={(e) => handleSafetyLeftChange(idx, 'today', e.target.value)} /></td>
                              <td><input type='text' className='form-control form-control-sm text-center bg-light' value={row.cumulative} readOnly /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Right table */}
                  <div className='col-md-6'>
                    <div className='table-responsive mb-3'>
                      <table className='table table-bordered table-sm align-middle'>
                        <thead className='table-light'>
                          <tr>
                            <th>Meetings & Inspections</th>
                            <th className='text-center'>Previous Day</th>
                            <th className='text-center'>Today</th>
                            <th className='text-center'>Cumulative</th>
                          </tr>
                        </thead>
                        <tbody>
                          {safetyRight.map((row, idx) => (
                            <tr key={idx}>
                              <td className='text-nowrap small'>{row.particulars}</td>
                              <td><input type='text' className='form-control form-control-sm text-center' value={row.previousDay} onChange={(e) => handleSafetyRightChange(idx, 'previousDay', e.target.value)} /></td>
                              <td><input type='text' className='form-control form-control-sm text-center' value={row.today} onChange={(e) => handleSafetyRightChange(idx, 'today', e.target.value)} /></td>
                              <td><input type='text' className='form-control form-control-sm text-center bg-light' value={row.cumulative} readOnly /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Equipment standby */}
                <h6 className='mb-2'>Key Equipment Status</h6>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle' style={{ maxWidth: '400px' }}>
                    <thead className='table-light'>
                      <tr>
                        <th>Equipment</th>
                        <th className='text-center'>Standby* (Y/N)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipmentRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className='small'>{row.equipment}</td>
                          <td>
                            <SearchableSelect className='form-select form-select-sm text-center' value={row.standbyYN} onChange={(e) => handleEquipmentChange(idx, 'standbyYN', e.target.value)}>
                              <option value='Y'>Y</option>
                              <option value='N'>N</option>
                            </SearchableSelect>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <small className='text-muted'>* If any equip is not in readiness, please give reasons in the Remarks column</small>
                </div>
              </div>
            )}

            {/* ─────────── TAB 9: Passenger Details ─────────── */}
            {activeTab === 'passengerDetails' && (
              <div>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <h6 className='m-0'>9. PASSENGER LIST</h6>
                  <button className='btn btn-sm btn-primary' onClick={addPassengerRow}>+ Add Row</button>
                </div>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th className='text-center' style={{ width: '60px' }}>Sr.No</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Position</th>
                        <th style={{ width: '120px' }}>DOJ</th>
                        <th style={{ width: '60px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {passengerRows.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            <input type='text' className='form-control form-control-sm text-center bg-light' value={idx + 1} readOnly />
                          </td>
                          <td>
                            <input type='text' className='form-control form-control-sm' placeholder='Name' value={row.name} onChange={(e) => handlePassengerChange(idx, 'name', e.target.value)} />
                          </td>
                          <td>
                            <input type='text' className='form-control form-control-sm' placeholder='Company' value={row.company} onChange={(e) => handlePassengerChange(idx, 'company', e.target.value)} />
                          </td>
                          <td>
                            <input type='text' className='form-control form-control-sm' placeholder='Position' value={row.position} onChange={(e) => handlePassengerChange(idx, 'position', e.target.value)} />
                          </td>
                          <td>
                            <FormattedDateInput className='form-control form-control-sm text-center' value={row.doj} onChange={(val: string) => handlePassengerChange(idx, 'doj', val)} />
                          </td>
                          <td className='text-center'>
                            <button className='btn btn-sm btn-outline-danger' onClick={() => removePassengerRow(idx)} title='Remove' disabled={passengerRows.length === 1}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 10: Engine Running Hours ─────────── */}
            {activeTab === 'engineRunningHours' && (
              <div>
                <h6 className='fw-bold mb-3'>10. ENGINE RUNNING HOURS</h6>
                <div className='table-responsive mb-4'>
                  <table className='table table-bordered table-sm align-middle text-center mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th style={{ width: '150px' }}>LABEL</th>
                        <th>OPENING R/Hr</th>
                        <th>DAILY R/Hr</th>
                        <th>TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {engineRunningHoursRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className='bg-light fw-bold small'>{row.label}</td>
                          <td>
                            <input type='number' min={0} max={25} step='any' className={`form-control form-control-sm text-center ${engineRunningHoursErrors[`${idx}-openingHours`] ? 'is-invalid' : ''}`} value={row.openingHours} onChange={(e) => handleEngineRunningHoursChange(idx, 'openingHours', e.target.value)} />
                            {engineRunningHoursErrors[`${idx}-openingHours`] && (
                              <div className='invalid-feedback d-block text-start'>
                                {engineRunningHoursErrors[`${idx}-openingHours`]}
                              </div>
                            )}
                          </td>
                          <td>
                            <input type='number' min={0} max={25} step='any' className={`form-control form-control-sm text-center ${engineRunningHoursErrors[`${idx}-dailyHours`] ? 'is-invalid' : ''}`} value={row.dailyHours} onChange={(e) => handleEngineRunningHoursChange(idx, 'dailyHours', e.target.value)} />
                            {engineRunningHoursErrors[`${idx}-dailyHours`] && (
                              <div className='invalid-feedback d-block text-start'>
                                {engineRunningHoursErrors[`${idx}-dailyHours`]}
                              </div>
                            )}
                          </td>
                          <td className='bg-light fw-bold'>{row.totalHours}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 11: Engine Param ─────────── */}
            {activeTab === 'engineParam' && (
              <div>
                <h6 className='fw-bold mb-3'>11. ENGINE PARAMETER</h6>
                <div className='table-responsive mb-4'>
                  <table className='table table-bordered table-sm align-middle text-center mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th style={{ width: '150px' }}>LABEL</th>
                        <th>LO PRES BAR</th>
                        <th>JCW COOLER °C</th>
                        <th>LO Temp cooler °C</th>
                      </tr>
                    </thead>
                    <tbody>
                      {engineParamRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className='bg-light fw-bold small'>{row.label}</td>
                          <td>
                            <input type='text' className='form-control form-control-sm text-center' value={row.loPres} onChange={(e) => handleEngineParamChange(idx, 'loPres', e.target.value)} />
                          </td>
                          <td>
                            <input type='text' className='form-control form-control-sm text-center' value={row.jcwCooler} onChange={(e) => handleEngineParamChange(idx, 'jcwCooler', e.target.value)} />
                          </td>
                          <td>
                            <input type='text' className='form-control form-control-sm text-center' value={row.loTempCooler} onChange={(e) => handleEngineParamChange(idx, 'loTempCooler', e.target.value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─────────── TAB 12: Exhaust Temp ─────────── */}
            {activeTab === 'exhTemp' && (
              <div>
                <h6 className='fw-bold mb-3'>12. EXHAUST TEMPERATURE of all units</h6>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle text-center mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th style={{ width: '150px' }}>LABEL</th>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((unit) => (
                          <th key={unit}>{unit}</th>
                        ))}
                        <th style={{ width: '120px' }}>Min</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exhTempRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className='bg-light fw-bold small text-nowrap'>{row.label}</td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.unit1} onChange={(e) => handleExhTempChange(idx, 'unit1', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.unit2} onChange={(e) => handleExhTempChange(idx, 'unit2', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.unit3} onChange={(e) => handleExhTempChange(idx, 'unit3', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.unit4} onChange={(e) => handleExhTempChange(idx, 'unit4', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.unit5} onChange={(e) => handleExhTempChange(idx, 'unit5', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.unit6} onChange={(e) => handleExhTempChange(idx, 'unit6', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.unit7} onChange={(e) => handleExhTempChange(idx, 'unit7', e.target.value)} /></td>
                          <td><input type='text' className='form-control form-control-sm text-center' value={row.unit8} onChange={(e) => handleExhTempChange(idx, 'unit8', e.target.value)} /></td>
                          <td className='bg-light fw-bold'>{getExhTempMinValue(row)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ── Footer buttons ── */}
          <div className='d-flex gap-2 justify-content-end mt-3 p-3'>
            <button className='btn btn-secondary' onClick={onClose}>
              Cancel
            </button>

            {activeTab !== TABS[0] && (
              <button className='btn btn-light' onClick={prevTab}>
                ← Back
              </button>
            )}

            {/* Always allow saving as draft from any tab */}
            <button 
              className='btn btn-warning' 
              onClick={() => void handleFinalSave('DRAFT')}
              title='Save as draft to edit later'
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save as Draft'}
            </button>

            {!isLastTab && (
              <button className='btn btn-primary' onClick={nextTab}>
                Next →
              </button>
            )}

            {isLastTab && (
              <button className='btn btn-success' onClick={() => void handleFinalSave('SUBMITTED')} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                    Saving…
                  </>
                ) : (
                  editData ? 'Update' : 'Submit'
                )}
              </button>
            )}
          </div>
        </div>

        {/* ── Granular SIRE 2.0 Change History Drawer ── */}
        <DprAuditHistoryDrawer
          show={showHistoryDrawer}
          onClose={() => setShowHistoryDrawer(false)}
          reportId={reportId}
        />
      </div>
    )
  }

  export default AddDprModal

